
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BookingSplitRow, Report, UserProfile, ReportMetadata, ServiceItem, InspectionItem, GuestParticipant, Contact, ProjectPlan } from '../types';
import type { ListProject } from '../services/listProject';
import { InspectionList } from './InspectionList';
import { FreimeldungPanel } from './freimeldung/FreimeldungPanel';
import { useFreimeldungen } from '../hooks/useFreimeldungen';
import { freimeldungLaufHinweis, freimeldungNachweisHinweis, istFreimeldungAktiv, nachweisErlaubtBuchung, verwaiste, warteschlangeBereinigen, warteschlangeEinreihen, warteschlangeVersuchZaehlen, type FreimeldungEntscheidungAuftrag } from '../services/freimeldungLogic';
import { mangelServerStatus } from '../services/freimeldungService';
import { ChecklistView } from './ChecklistView';
import { ContactsView } from './ContactsView';
import { SafetyPointsView } from './SafetyPointsView';
import { HazardMatrixView } from './HazardMatrixView';
import { AnalysisView } from './AnalysisView';
import { ProjectLocationView } from './ProjectLocationView';
import { HistoryView } from './HistoryView';
import { PdfPreviewModal } from './PdfPreviewModal';
import { EmailModal } from './EmailModal';
import { SettingsForm } from './SettingsForm';
import { BillingSelectionModal } from './BillingSelectionModal';
import { AdditionalServicesModal } from './AdditionalServicesModal';
import { PrintSettingsModal } from './PrintSettingsModal';
import { fetchReportHistoryList, loadReportSnapshot, loadProjectById, saveProjectToCloud, uploadAsset, deleteReportHistory } from '../services/cloudService';
import { buchungstag, childBookingDescription, isAccountingEntry } from '../services/serviceLogUtils';
import { getServiceLog, mirrorEntriesToJournal } from '../services/serviceJournal';
import { isHourlyUnit } from '../services/projectLv';
import { LinkedBillingMode, TAGESPAUSCHALE, EINZELN, TochterSperrgrund, billingModeAt, childIdFromReportRef, sperrtEigeneBegehung, sperrtFremdeBuchungDieTochter } from '../services/linkedBillingMode';
import { buildActorName, realActorName } from '../services/actorName';
import { fetchChildBillingModes } from '../services/linkedBillingModeService';
import {
    beschreibeNummernWechsel,
    eintraegeKollisionsfreiEinordnen,
    EinordnungsErgebnis,
    neuenEintragEinfuegen,
    statusWechselAnwenden,
    freimeldeDatum,
} from '../services/inspectionItemStatus';
import { ParticipantSheet } from './contacts/ParticipantSheet';
import { consumeParticipantPrompt, participantPromptKey } from '../services/participantPrompt';
import { checkAlreadySent, SendGuardDecision } from '../services/sendGuard';
import { istUebungsprojekt } from '../services/uebung/uebungService';
import { checkReportRelease, ReleaseDecision } from '../services/reportReleaseGuard';
import { DraftDiscardPlanOk, planDraftDiscard } from '../services/draftDiscardLogic';
import { collectDraftDiscardEvidence, DraftDiscardError, executeDraftDiscard } from '../services/draftDiscardService';
import { FileText, Mail, ArrowLeft, Loader2, SlidersHorizontal, Globe, AlertTriangle, Trash2 } from 'lucide-react';
import { ProjectNoticeBanner } from './ProjectNoticeBanner';
import { NO_PROJECT_NOTICE, ProjectNotice, readOnlyProjectNotice } from '../services/projectLockLogic';
import { decideMobileWizardGuard } from '../services/mobileWizardLock';
import { holdIdleExit, UPLOAD_HALTE, uploadBeginnt, uploadLaeuft } from '../services/idleHold';
import { offenerDialogImDom } from '../services/appUmfang';
import { anEingehaengteInstanz, registriereSpaetEmpfaenger } from '../services/spaetEmpfaenger';
import { READ_ONLY_BANNER_MODUL, readOnlyMessage } from '../services/readOnlyNotice';
import { MobileInspectionWizard } from './mobile/MobileInspectionWizard'; // NEW IMPORT
import { MobileChecklistMode } from './mobile/MobileChecklistMode';
import { SyncBadge } from './common/SyncBadge';
import { useIsMobile } from '../hooks/useIsMobile'; // NEW IMPORT
import { useRechte } from '../hooks/useRechte';
import { fetchKoordinierungPaare, updateKoordinierungPaar } from '../services/koordinierungService';
import type { KoordinierungPaar } from './koordinierung/koordinierungLogic';
import { additionalServiceCandidates } from '../services/additionalServiceCandidates';
import { isInspectionProofEntry, isReportProofEntry, planFlatProofEntry } from '../services/flatRateLogic';
import { preferredInspectionServiceIds, selectInspectionSendServices } from '../services/billingSelection';
import { flatTargetForInspectionChoice } from '../services/reportBooking';
import { VerortungEinrichtung } from './verortung/VerortungEinrichtung';
import { verortungLesen } from '../services/verortung';

/**
 * Wie oft eine wartende Freimelde-Entscheidung nachfragt, ob der Mangel
 * inzwischen serverseitig geschlossen ist. 30 Sekunden: Eine Lesefrage auf eine
 * einzelne Zeile, und nur solange ueberhaupt etwas in der Schlange steht —
 * haeufiger braucht es niemand, seltener liesse die Entscheidung spuerbar
 * liegen.
 */
const FREIMELDUNG_NACHFASS_MS = 30_000;
/**
 * Hoechstdauer eines Upload-Halts (holdIdleExit): haengt ein Upload ohne
 * Ende, blieben sonst Leerlauf-Rueckzug, Neuladen und „+“ dauerhaft gesperrt.
 */
const UPLOAD_HALT_MS = 120_000;

/**
 * Das Projekt, wie App.tsx es hereinreicht: der Listeneintrag, dessen `data`
 * durch den geoeffneten Bericht ersetzt ist. Gelesen werden nur `id` und
 * `data` — der Rest des Auszugs spielt hier keine Rolle.
 */
type InspectionProject = Pick<ListProject, 'id'> & { data: Report };

interface Props {
    project: InspectionProject;
    userProfile: UserProfile | null;
    onBack: () => void;
    activeSection: string;
    onSectionChange: (section: string) => void;
    onUpdate: (report: Report) => void;
    /** SaveStatus plus 'read-only' — der Lesemodus braucht dieselbe Anzeige. */
    saveStatus?: import('../hooks/useOfflineSync').SaveStatus | 'read-only';
    /**
     * Schreibgeschuetzt geoeffnetes Projekt (z. B. aus der Offene-Posten-Sicht).
     * App.handleUpdateReport verwirft in diesem Zustand jeden Save still — die
     * betroffenen Werkzeuge duerfen deshalb erst gar nicht bedienbar sein.
     */
    isReadOnly?: boolean;
    /**
     * Fertiger Hinweisbalken (Nur-Ansicht ODER fremde Projektsperre). Die
     * Entscheidung faellt in App.tsx, weil nur dort Sperrzeile, Rolle und
     * Grund des Schreibschutzes zusammenkommen.
     */
    notice?: ProjectNotice;
    /** Uebernahme der Bearbeitung; ohne Handler erscheint kein Knopf. */
    onTakeover?: () => void;
    /** Begruendung fuer ein fremdes Projekt (SQL Nr. 128); ohne Handler kein Knopf. */
    onJustify?: () => void;
    takeoverBusy?: boolean;
    /**
     * Spaete Ergebnisse, die erst NACH dem Aushaengen ankommen (Foto-Upload,
     * Assistent nach „Fertig", Beweisbild): App.tsx wendet sie auf ihren
     * aktuellen Bericht an — und nur, solange dasselbe Projekt offen ist.
     * false = verworfen (Projekt gewechselt oder geschlossen).
     */
    onSpaetAendern?: (aendern: (report: Report) => Report) => boolean;
}


/**
 * Liegt die Buchung am Begehungstag? Beide Seiten als Tag in Europe/Berlin
 * (`buchungstag`, S5d) — dieselbe Rechnung wie `book()`, das Nachbuchen, der
 * Nachtlauf und die Journal-Spalte `entry_day`. Bis S5d galt hier „UTC-Praefix
 * ODER Geraetezeit": eine Buchung von 00:30 Uhr am 06.05. (UTC noch der 05.05.)
 * lag damit an BEIDEN Tagen und sperrte die Tagespauschale des Vortags mit.
 */
const isSameDay = (isoDateString: string | undefined, targetDateYMD: string): boolean => {
    const tag = buchungstag(isoDateString);
    return !!tag && tag === buchungstag(targetDateYMD);
};

export const SiGeKoInspectionModule: React.FC<Props> = ({ project, userProfile, onBack, activeSection, onSectionChange, onUpdate, saveStatus, isReadOnly = false, notice, onTakeover, onJustify, takeoverBusy = false, onSpaetAendern }) => {
    // Initialize report state once from props. Subsequent updates handled locally or via explicit re-fetch.
    const [report, setReport] = useState<Report | null>(() => project.data || null);
    /**
     * Der jeweils aktuelle Bericht auch fuer Rueckrufe, die laenger laufen als
     * ein Render. Der mobile Wizard quittiert synchron „Gespeichert", schreibt
     * aber erst nach dem Foto-Upload: ein Closure-Wert waere dort der Stand von
     * vor dem vorigen Eintrag — der erste Mangel fiele beim Schreiben heraus.
     * `handleUpdate` zieht den Ref sofort nach, nicht erst beim Re-Render.
     */
    const reportRef = useRef<Report | null>(report);
    reportRef.current = report;
    /**
     * Steht das Modul noch? Ausgehaengt traegt `reportRef` den Stand beim
     * Verlassen und `onUpdate` die Closure des letzten Renders: ein spaetes
     * Ergebnis darueber setzte den App-Bericht zurueck, nach einem
     * Projektwechsel sogar auf dieses Projekt. Dann gilt `onSpaetAendern`.
     */
    const montiertRef = useRef(false);
    useEffect(() => {
        montiertRef.current = true;
        return () => { montiertRef.current = false; };
    }, []);
    // Spaete Ergebnisse einer frueheren, ausgehaengten Instanz dieses Projekts
    // (spaetEmpfaenger.ts) auf den Stand DIESER Instanz — wie jede Eingabe.
    const spaetEmpfangRef = useRef<(aendern: (r: Report) => Report) => void>(() => {});
    useEffect(() => {
        spaetEmpfangRef.current = (aendern) => {
            const aktuell = reportRef.current;
            if (!aktuell) return;
            const neu = aendern(aktuell);
            if (neu === aktuell) return;
            reportRef.current = neu;
            setReport(neu);
            onUpdate(neu);
        };
    });
    useEffect(() => registriereSpaetEmpfaenger(project.id, (aendern) => spaetEmpfangRef.current(aendern)), [project.id]);
    /**
     * Spaetes Ergebnis nach dem Aushaengen: an die inzwischen eingehaengte
     * Instanz desselben Projekts, sonst an App.tsx (nur solange das Projekt
     * offen ist). false = verworfen.
     */
    const spaetWeitergeben = (aendern: (r: Report) => Report): boolean =>
        anEingehaengteInstanz(project.id, aendern) || (onSpaetAendern?.(aendern) ?? false);
    const [isLoading, setIsLoading] = useState(!project.data?.items);
    // Preise im Abrechnungsdialog nur mit dem Recht „Preise sehen" (Admins immer).
    const rechte = useRechte(userProfile?.role);
    
    // Modal States
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [showAdditionalModal, setShowAdditionalModal] = useState(false);
    const [showPrintSettings, setShowPrintSettings] = useState(false);
    const [history, setHistory] = useState<any[]>([]);
    // Der Versand-Ablauf (Prüfung → Abrechnung → Zusatzleistungen → E-Mail) läuft
    // über mehrere Modals und asynchrone Schritte. Solange irgendein Schritt
    // offen ist, muss "Abschließen & Senden" gesperrt bleiben: ein zweiter
    // Durchlauf erzeugt eine zweite Buchung bzw. einen zweiten Versand.
    const [isCheckingSendGuard, setIsCheckingSendGuard] = useState(false);
    const [sendFlowActive, setSendFlowActive] = useState(false);
    const [sendGuardWarning, setSendGuardWarning] = useState<SendGuardDecision | null>(null);
    // Freigabe-Sperre fuer Berichte aus Freelancer-Begehungen (ERP-Plan 7,
    // Schritt 4): erst nach der Vier-Augen-Pruefung darf ein solcher Bericht an
    // den Kunden. Anders als sendGuardWarning gibt es hier KEIN „trotzdem
    // senden" — die Pruefung ist der Zweck, nicht eine Empfehlung.
    const [releaseBlock, setReleaseBlock] = useState<ReleaseDecision | null>(null);
    /**
     * „Entwurf verwerfen" — der Rueckweg von „Naechste Begehung anlegen".
     * Erst die Pruefung (checking), dann entweder der Bestaetigungsdialog mit
     * dem fertigen Plan oder der Grund, warum nicht (blocked). Waehrend des
     * Schreibens (busy) bleibt der Dialog gesperrt — ein zweiter Klick
     * darf nicht zweimal zuruecksetzen. Regeln: services/draftDiscardLogic.
     */
    const [draftDiscard, setDraftDiscard] = useState<
        | { kind: 'checking' }
        | { kind: 'confirm'; plan: DraftDiscardPlanOk }
        | { kind: 'busy'; plan: DraftDiscardPlanOk }
        | { kind: 'blocked'; message: string }
        | null
    >(null);
    const draftDiscardBusyRef = useRef(false);
    /**
     * Der Schreibschutz, wie er JETZT ist — fuer Rueckrufe, die laenger laufen
     * als ein Render. `isReadOnly` in der Closure ist der Stand vom Klick;
     * kippt die Projektsperre waehrend der Pruefung (Uebernahme durch einen
     * anderen), darf das Verwerfen danach nicht mehr beginnen.
     */
    const isReadOnlyRef = useRef(isReadOnly);
    isReadOnlyRef.current = isReadOnly;
    const [isRepeatSend, setIsRepeatSend] = useState(false);
    const sendGuardRef = useRef(false);
    // Verzoegertes Oeffnen des E-Mail-Fensters (siehe handleCompleteFlow): der
    // Timer muss abraeumbar sein, sonst geht das Fenster nach einem Abbruch
    // oder nach dem Verlassen des Moduls trotzdem noch auf.
    const emailModalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /**
     * Teilnehmerauswahl, die sich beim Anlegen der Begehung von selbst oeffnet.
     *
     * Der Folgebericht startet ohne Anwesenheiten und ohne Gaeste (Reset in
     * useReportActions). Wurde er gerade eben angelegt, wird die Frage hier
     * einmal gestellt, statt sie im Reiter „Verteiler" auf den Pruefer warten
     * zu lassen — dort ist sie auf der Baustelle regelmaessig untergegangen.
     * Der Merker wird genau einmal eingeloest, ein spaeterer Aufruf des Moduls
     * reisst die Auswahl nicht erneut auf.
     */
    const [showParticipantPrompt, setShowParticipantPrompt] = useState(false);
    const participantPromptCheckedRef = useRef(false);
    const [showMobileWizard, setShowMobileWizard] = useState(false);
    const [wizardInitialData, setWizardInitialData] = useState<Partial<import('../types').InspectionItem> | null>(null);
    /**
     * Bestaetigte Gewerkepaare aus dem Modul „Gewerke-Ueberschneidung", allein
     * fuer den Reiter „Matrix". Solange die Liste leer ist, ist die
     * Gefaehrdungsmatrix Zeichen fuer Zeichen die bisherige — das Hinweisband
     * haengt dort an der Zahl der Kandidaten.
     */
    const [koordinierungPaare, setKoordinierungPaare] = useState<KoordinierungPaar[]>([]);
    /**
     * Ein Ladefehler, den der Dienst NICHT selbst abfaengt.
     *
     * Die noch nicht gefahrene Migration Nr. 40 faengt `ladePaare` ab und gibt
     * eine leere Liste zurueck — die bleibt stumm, so ist es gewollt. Was hier
     * ankommt, ist etwas anderes: RLS, Netz, ein Serverfehler. Das darf nicht
     * nur in der Konsole stehen, sonst sieht der Reiter aus, als gaebe es keine
     * bestaetigten Paare.
     */
    const [koordinierungFehler, setKoordinierungFehler] = useState(false);
    /** Projekt, fuer das die Paare bereits geholt wurden. Verhindert, dass
     *  jeder Wechsel auf den Reiter eine neue Abfrage ausloest. */
    const koordinierungGeholtRef = useRef<string | null>(null);

    /**
     * Solange der Erfassungs-Assistent offen ist, darf sich das Projekt nicht
     * von selbst schliessen.
     *
     * Der Leerlauf-Rueckzug (30 Minuten ohne Eingabe, App.tsx) haengt die
     * Projektansicht aus — und mit ihr diesen Assistenten samt allem, was
     * gerade in seiner Maske steht. Genau das verhindert mobileWizardLock
     * bereits beim Kippen der Sperre: die Maske bleibt stehen, damit der
     * Pruefer seine Eingaben noch retten kann. Ein automatischer Rueckzug
     * duerfte diese Zusage nicht hintenherum brechen.
     */
    useEffect(() => {
        if (!showMobileWizard) return;
        return holdIdleExit('mobile-inspection-wizard');
    }, [showMobileWizard]);
    
    useEffect(() => {
        if (participantPromptCheckedRef.current) return;
        const nr = report?.metadata?.inspectionNumber;
        if (!report || nr === undefined) return; // Bericht wird noch geladen.
        participantPromptCheckedRef.current = true;
        // Im Lesemodus wird der Merker trotzdem verbraucht: die Auswahl waere
        // dort ohnehin nur zum Ansehen und soll nicht spaeter aufspringen.
        if (isReadOnly) return;
        if (consumeParticipantPrompt(participantPromptKey(project.id, nr))) setShowParticipantPrompt(true);
    }, [report, isReadOnly, project.id]);

    const isMobile = useIsMobile();
    
    // Parent Project (if linked)
    const [parentProjectData, setParentProjectData] = useState<Report | null>(null);
    const [parentServices, setParentServices] = useState<ServiceItem[]>([]);
    
    // Blocked services map for billing (ID -> Reason)
    const [blockedServices, setBlockedServices] = useState<Record<string, { reason: string }>>({});

    // Preview Report from History
    const [previewReport, setPreviewReport] = useState<Report | null>(null);

    // „PDF Export" (Seitenleiste, Abschnitt 'export') öffnet beim WECHSEL
    // dorthin die Vorschau des laufenden Berichts. Nicht beim Einhängen mit
    // 'export' (Wiederherstellen nach dem Neuladen): das PDF entstuende sonst
    // ungefragt neu. Dahinter steht der Abschnitt mit „PDF Vorschau öffnen".
    const [vorigerAbschnitt, setVorigerAbschnitt] = useState(activeSection);
    // Woher „PDF Export" kam: das Schließen der Vorschau führt dorthin zurück —
    // bliebe 'export' stehen, täte ein zweiter Klick darauf nichts.
    const [abschnittVorExport, setAbschnittVorExport] = useState<string | null>(null);
    if (activeSection !== vorigerAbschnitt) {
        setVorigerAbschnitt(activeSection);
        if (activeSection === 'export') {
            setAbschnittVorExport(vorigerAbschnitt);
            setPreviewReport(null);
            setShowPdfPreview(true);
        }
    }

    // TEMPORARY STATE FOR BILLING FLOW
    const [precapturedHours, setPrecapturedHours] = useState<number>(1);
    const [precapturedServiceId, setPrecapturedServiceId] = useState<string | null>(null);
    /**
     * Aufteilung der Begehung auf mehrere Lose. Leere Liste = keine Buchung,
     * eine Zeile = der Normalfall (dann sind serviceId/hours oben dieselben
     * Werte). Die Liste ist die Quelle der Wahrheit fuer den Versand.
     */
    const [precapturedBookings, setPrecapturedBookings] = useState<BookingSplitRow[]>([]);
    const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>({});

    // Effect: Load full project data if needed (e.g. deep refresh or missing items)
    useEffect(() => {
        const initReport = async () => {
            const hasPlaceholders = project.data?.items?.some((i: any) => Array.isArray(i.photos) && i.photos.includes('placeholder'));
            
            if (project.data?.items && project.data?.checklists && !hasPlaceholders) {
                // Data is already robust and has no placeholders, no need to fetch
                setReport(project.data);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const fullProject = await loadProjectById(project.id, undefined, { skipMigration: isReadOnly });
                let dataToUse: Report;

                if (fullProject && fullProject.data) {
                    dataToUse = fullProject.data;
                } else {
                    // CRITICAL: Do not fallback to project.data if it has placeholders!
                    if (hasPlaceholders) {
                        throw new Error("Vollständige Projektdaten konnten nicht geladen werden und lokale Daten sind unvollständig.");
                    }
                    dataToUse = project.data;
                }

                // Ensure data structure integrity
                const initializedReport: Report = {
                    ...dataToUse,
                    items: dataToUse.items || [],
                    checklists: dataToUse.checklists || [],
                    metadata: dataToUse.metadata || ({} as ReportMetadata),
                    settings: { 
                        ...dataToUse.settings, 
                        appMode: userProfile?.app_mode || 'sigeko',
                        emailSignatureTemplate: userProfile?.settings?.emailSignatureTemplate || dataToUse.settings?.emailSignatureTemplate || ''
                    }
                };

                setReport(initializedReport);
                // Sync back to parent only if data structure changed
                onUpdate(initializedReport);
            } catch (e) {
                console.error("Failed to load project details", e);
                alert("Fehler beim Laden der Projektdaten. Bitte prüfen Sie Ihre Internetverbindung.");
                onBack(); // Return to safety
            } finally {
                setIsLoading(false);
            }
        };

        if (project.id) {
            initReport();
            
            // Archivliste im Hintergrund: nur Kopf und Metadaten, NICHT die
            // Schnappschuesse. Bis 30.09.2026 lud das Modul hier bei jedem
            // Oeffnen alle Archivberichte samt Maengelbestand (6051: 110
            // Berichte mit je bis zu 640 Maengeln) und lief danach rekursiv
            // ueber jeden Wert — der Bericht fror beim Oeffnen ein. Den vollen
            // Schnappschuss laedt HistoryView ueber `resolveReport` erst, wenn
            // er gebraucht wird (PDF, Maengel wiederherstellen).
            fetchReportHistoryList(project.id).then(setHistory).catch(console.error);
        }
    }, [project.id]); // Removed userProfile dependency to prevent loop

    /**
     * Die Koordinierungspaare werden ERST geholt, wenn der Reiter „Matrix"
     * wirklich benutzt wird.
     *
     * Sie liegen in einer eigenen Tabelle und gehen keinen der uebrigen Reiter
     * etwas an; sie beim Oeffnen jedes Berichts mitzuladen, zahlte die
     * Baustelle auf schmaler Leitung mit. Im Lesemodus wird gar nicht geladen:
     * die einzige Handlung am Hinweisband ist die Uebernahme, und die schriebe
     * dort ins Leere — ohne Paare zeigt die Matrix das Band erst gar nicht.
     */
    useEffect(() => {
        if (activeSection !== 'interactions' || isReadOnly || !project.id) return;
        if (koordinierungGeholtRef.current === project.id) return;
        // Beim Projektwechsel zuerst den alten Stand raeumen, sonst stuenden
        // kurz die Paare des vorigen Projekts im Band.
        if (koordinierungGeholtRef.current !== null) setKoordinierungPaare([]);
        koordinierungGeholtRef.current = project.id;
        setKoordinierungFehler(false);
        let abgebrochen = false;
        fetchKoordinierungPaare(project.id)
            .then(paare => { if (!abgebrochen) setKoordinierungPaare(paare); })
            .catch(e => {
                // Kein Abbruch der Matrix: ohne Paare ist sie die Ansicht von
                // heute und bleibt vollstaendig benutzbar. Aber auch kein
                // stilles Schlucken mehr — die fehlende Tabelle faengt der
                // Dienst selbst ab, hier kommt nur Unerwartetes an, und das
                // muss jemand sehen. Der Merker faellt zurueck, damit ein
                // spaeterer Besuch des Reiters es erneut versuchen kann.
                koordinierungGeholtRef.current = null;
                console.error('Koordinierungspaare konnten nicht geladen werden', e);
                if (!abgebrochen) setKoordinierungFehler(true);
            });
        return () => { abgebrochen = true; };
    }, [activeSection, isReadOnly, project.id]);

    // Verzoegertes E-Mail-Fenster beim Verlassen des Moduls abraeumen.
    useEffect(() => () => {
        if (emailModalTimerRef.current) {
            clearTimeout(emailModalTimerRef.current);
            emailModalTimerRef.current = null;
        }
    }, []);

    // LOAD PARENT PROJECT IF LINKED
    useEffect(() => {
        const config = report?.metadata.globalData?.linkedProjectConfig;
        if (config && config.role === 'child' && config.linkedProjectId) {
            // Das Mutterprojekt wird hier nur zum Anzeigen gelesen — im
            // Lesemodus erst recht ohne Schreibzugriff auf fremde Daten.
            loadProjectById(config.linkedProjectId, undefined, { skipMigration: isReadOnly }).then(parent => {
                if (parent && parent.data) {
                    setParentProjectData(parent.data);
                    setParentServices(parent.data.metadata.globalData?.services || []);
                }
            }).catch(e => console.error("Failed to load parent", e));
        }
    }, [report?.metadata.globalData?.linkedProjectConfig?.linkedProjectId]);

    /**
     * Der Balken ueber der Werkzeugleiste. Kommt fertig von App.tsx, damit
     * dort EINE Stelle ueber Sperre und Nur-Ansicht entscheidet; ohne die
     * Angabe (Einsatz ausserhalb von App) bleibt der bisherige Nur-Ansicht-
     * Hinweis.
     */
    const bannerNotice = notice
        ?? (isReadOnly ? readOnlyProjectNotice(READ_ONLY_BANNER_MODUL) : NO_PROJECT_NOTICE);

    const handleDeleteHistory = async (id: string) => {
        // Schreibt direkt in die Cloud, also am Lesemodus von
        // App.handleUpdateReport vorbei: der Archiveintrag waere endgueltig weg.
        // HistoryView blendet den Loesch-Knopf im Lesemodus aus und stellt die
        // Rueckfrage gar nicht erst; dieser Riegel bleibt als zweite Reihe.
        if (isReadOnly) {
            alert(readOnlyMessage('Archiveinträge lassen sich in dieser Ansicht nicht löschen.'));
            return;
        }
        const success = await deleteReportHistory(id);
        if (success) {
            setHistory(prev => prev.filter(h => h.id !== id));
        } else {
            alert("Fehler beim Löschen des Eintrags.");
        }
    };

    const handleUpdate = (updates: Partial<Report>) => {
        if (!report) return;
        const updatedReport = { ...report, ...updates };
        reportRef.current = updatedReport;
        setReport(updatedReport);
        onUpdate(updatedReport); // Sync to App
    };

    /**
     * Spaete Ergebnisse der Maengelliste (Foto-Upload, KI-Vorschlag, URL des
     * Beweisbilds) laufen auf dem Stand aus `reportRef`: die Liste kann bei der
     * Antwort schon abgebaut sein (Assistent offen, Reiterwechsel), ihr
     * `onChange` truege dann einen alten Bericht. `aendern` wird synchron
     * aufgerufen — die Liste liest daraus den Rueckgaengig-Stand.
     */
    const maengelAktuellAendern = (aendern: (aktuell: InspectionItem[]) => InspectionItem[]) => {
        if (!montiertRef.current) {
            const angewendet = spaetWeitergeben(r => {
                const items = aendern(r.items);
                return items === r.items ? r : { ...r, items };
            });
            if (!angewendet) console.warn('Spätes Ergebnis der Mängelliste verworfen: Projekt nicht mehr offen.');
            return;
        }
        const aktuell = reportRef.current;
        if (!aktuell) return;
        const items = aendern(aktuell.items);
        if (items === aktuell.items) return;
        const updatedReport = { ...aktuell, items };
        reportRef.current = updatedReport;
        setReport(updatedReport);
        onUpdate(updatedReport);
    };

    const handleMetadataUpdate = (newMeta: Partial<ReportMetadata>) => {
        if (!report) return;
        handleUpdate({
            metadata: { ...report.metadata, ...newMeta }
        });
    };

    /**
     * Plan aus „Verortung auf Plan“ bzw. dem Handy-Assistenten hochgeladen
     * (docs/planverortung-vertrag.md, P3): an `metadata.plans` anhaengen.
     *
     * Stand aus `reportRef`, NICHT aus dem Closure-`report`: der Upload laeuft
     * asynchron, und in der Zwischenzeit koennen Nadeln, Texte oder ganze
     * Eintraege dazugekommen sein — ein veralteter Stand drehte sie zurueck.
     * Speichern wie `handleUpdate` (Ref, State, App).
     */
    const planHinzufuegen = (plan: ProjectPlan) => {
        const aktuell = reportRef.current;
        if (!aktuell) return;
        const vorhandene = aktuell.metadata.plans || [];
        if (vorhandene.some(p => p.id === plan.id)) return;
        const updatedReport = { ...aktuell, metadata: { ...aktuell.metadata, plans: [...vorhandene, plan] } };
        reportRef.current = updatedReport;
        setReport(updatedReport);
        onUpdate(updatedReport);
    };

    /**
     * Ein Entwurf ist ein Bericht mit Nummer, der noch nicht versendet ist.
     * Das JSON-Flag ist nur die Vorpruefung fuer den Knopf; ob er wirklich
     * noch keiner ist (Archiv, Sendebericht, Buchung, DB-Spalte), klaert die
     * Pruefung beim Klick — fail-closed.
     */
    const istEntwurf = !isReadOnly && !!report && (report.metadata.inspectionNumber || 0) >= 1 && !report.metadata.isExported;
    /**
     * Solange die Warteschlange den Entwurf noch hochlaedt, wird nicht
     * verworfen: ein laufender Save schriebe die Zeilen der Entwurfs-Eintraege
     * gleich wieder, und der Blob-Save landete hinter dem Spalten-Vorgriff.
     */
    // Alles ausser „nichts offen" zaehlt: auch 'error', 'offline', 'storage-error'
    // und 'auth-required' heissen, dass der Entwurf noch nicht sicher auf dem
    // Server liegt — und dann darf sein Rueckweg nicht beginnen.
    const speichertGerade = saveStatus !== undefined && saveStatus !== 'idle' && saveStatus !== 'saved' && saveStatus !== 'read-only';
    const SPEICHERT_NOCH = 'Der Entwurf wird gerade gespeichert. Bitte einen Moment warten und erneut versuchen.';

    const handleDiscardDraftStart = async () => {
        const aktuell = reportRef.current;
        if (!aktuell || isReadOnly || draftDiscard) return;
        if (speichertGerade || uploadLaeuft()) { setDraftDiscard({ kind: 'blocked', message: SPEICHERT_NOCH }); return; }
        setDraftDiscard({ kind: 'checking' });
        const evidence = await collectDraftDiscardEvidence(project.id, aktuell);
        const plan = planDraftDiscard(aktuell, project.id, evidence);
        setDraftDiscard(plan.ok === true ? { kind: 'confirm', plan } : { kind: 'blocked', message: plan.message });
    };

    const handleDiscardDraftConfirm = async () => {
        if (draftDiscard?.kind !== 'confirm') return;
        // Ref-Lock zuerst: der State greift erst nach dem Re-Render, zwei
        // Klicks im selben Tick saehen beide noch 'confirm' (Hausmuster
        // sendGuardRef / isSubmittingRef im LoadReportDialog).
        if (draftDiscardBusyRef.current) return;
        draftDiscardBusyRef.current = true;
        const aktuell = reportRef.current;
        if (!aktuell) { draftDiscardBusyRef.current = false; return; }
        // Der Schreibschutz kann gekippt sein, waehrend der Dialog offen war
        // (Sperre verloren, Leerlauf). Dann geht nichts mehr an die Datenbank —
        // App.handleUpdateReport wuerde den wiederhergestellten Bericht auch
        // nicht mehr einreihen, und Spalten und Blob liefen auseinander.
        if (isReadOnly) { setDraftDiscard({ kind: 'blocked', message: readOnlyMessage('Der Entwurf wurde nicht verworfen.') }); draftDiscardBusyRef.current = false; return; }
        if (speichertGerade || uploadLaeuft()) { setDraftDiscard({ kind: 'blocked', message: SPEICHERT_NOCH }); draftDiscardBusyRef.current = false; return; }
        setDraftDiscard({ kind: 'busy', plan: draftDiscard.plan });
        try {
            // FRISCH pruefen und planen, nicht den Plan aus dem Dialog nehmen:
            // waehrend er offen war, kann der Bericht anderswo versendet,
            // archiviert oder gebucht worden sein. Das bedingte Spalten-Update
            // in executeDraftDiscard haelt danach noch einmal gegen die
            // Datenbank — erst dann fallen Zeilen.
            const evidence = await collectDraftDiscardEvidence(project.id, aktuell);
            const plan = planDraftDiscard(aktuell, project.id, evidence);
            if (plan.ok !== true) { setDraftDiscard({ kind: 'blocked', message: plan.message }); return; }
            // Nach dem Await noch einmal: die Sperre kann waehrend der Pruefung
            // an jemand anderen gegangen sein — der Anker merkt das nicht, eine
            // Uebernahme schreibt das Projekt nicht.
            if (isReadOnlyRef.current) { setDraftDiscard({ kind: 'blocked', message: readOnlyMessage('Der Entwurf wurde nicht verworfen.') }); return; }
            // Der Dienst schreibt bestaetigt: Spalten-Vorgriff → Bericht
            // (saveProjectToCloud, mit Anker) → Zeilen der Kindtabellen. Erst
            // danach der lokale Stand und die Warteschlange: ihr wartender
            // Entwurfs-Eintrag wird so durch den wiederhergestellten ersetzt.
            await executeDraftDiscard(project.id, plan, aktuell);
            reportRef.current = plan.restored;
            setReport(plan.restored);
            onUpdate(plan.restored);
            setDraftDiscard(null);
            onBack();
        } catch (e) {
            console.error('Entwurf verwerfen fehlgeschlagen', e);
            // Der Dienst sagt selbst, was die Datenbank jetzt traegt (Entwurf
            // unveraendert oder uneinheitlich). Nur ein fremder Fehler bekommt
            // den allgemeinen Satz: der Entwurf bleibt lokal stehen, sein
            // naechster Save stellt die Spalten wieder auf ihn.
            const message = e instanceof DraftDiscardError
                ? e.message
                : `Der Entwurf konnte nicht verworfen werden. Bitte das Projekt neu laden und erneut versuchen. (${e instanceof Error ? e.message : String(e)})`;
            setDraftDiscard({ kind: 'blocked', message });
        } finally {
            draftDiscardBusyRef.current = false;
        }
    };

    const handleContactsUpdate = (newDistributionList: any[]) => {
        if (!report) return;
        const updatedReport = {
             ...report,
             metadata: { ...report.metadata, distributionList: newDistributionList }
        };
        setReport(updatedReport);
        onUpdate(updatedReport);
    };

    // Kontakte und Gaeste in EINEM Update: getrennt gesetzt baut der zweite
    // Aufruf sein Metadaten-Objekt aus demselben `report` wie der erste und
    // schreibt dessen Aenderung wieder weg.
    const handleParticipantsUpdate = (newDistributionList: Contact[], guests: GuestParticipant[]) => {
        if (!report) return;
        const updatedReport = {
            ...report,
            metadata: { ...report.metadata, distributionList: newDistributionList, guests }
        };
        setReport(updatedReport);
        onUpdate(updatedReport);
    };

    /**
     * Oeffnet den Erfassungs-Wizard. Im Lesemodus gar nicht erst: der Wizard
     * laedt die Fotos VOR dem Speichern in den Storage hoch und meldet danach
     * "Gespeichert" — der Mangel verschwindet aber beim Verlassen und die
     * Bilder blieben als Waisen im Bucket liegen.
     */
    const openMobileWizard = (initial?: Partial<import('../types').InspectionItem>) => {
        if (isReadOnly) {
            alert(readOnlyMessage('Neue Einträge lassen sich hier nicht erfassen.'));
            return;
        }
        setWizardInitialData(initial ?? null);
        setShowMobileWizard(true);
    };

    // Intercept standard add item to show wizard on mobile
    const handleAddItemWrapper = () => {
        openMobileWizard();
    };

    /**
     * Der Assistent zeigt „Erfasst – wird gespeichert", bis dieses Promise
     * erfuellt ist, und erst dann „Gespeichert". Jeder Weg, der NICHT
     * schreibt, muss deshalb werfen — ein stilles `return` meldete Erfolg.
     */
    const handleMobileWizardSave = async (item: any) => {
        if (!report) throw new Error('Bericht nicht geladen. Eintrag nicht gespeichert.');
        // Zweite Sperre direkt vor dem Upload: der Schreibschutz kann kippen,
        // waehrend der Assistent offen ist — ohne Guard entstuenden hier
        // Storage-Waisen zu einem Mangel, den niemand mehr sieht.
        if (isReadOnly) {
            alert(readOnlyMessage('Der Eintrag wurde NICHT gespeichert und die Fotos wurden nicht hochgeladen.'));
            throw new Error('Nur Ansicht. Eintrag nicht gespeichert.');
        }

        // Bis hier geschrieben ist, liegt der Eintrag nur in diesem Aufruf —
        // auch nach „Fertig“: kein Neuladen (Update-Balken, Deployment) und
        // kein Leerlauf-Rückzug dazwischen.
        const loesen = holdIdleExit(UPLOAD_HALTE.eintrag);
        const obergrenze = window.setTimeout(loesen, UPLOAD_HALT_MS);
        // Senden/Verwerfen bleiben bis zum echten Ende gesperrt (uploadLaeuft).
        const uploadEnde = uploadBeginnt();
        try {
            // Upload photos if they are Base64
            // This ensures new mobile entries use Storage URLs
            const processedPhotos = await Promise.all((item.photos || []).map(async (p: string) => {
                 return await uploadAsset(p, project.id);
            }));
        
            let processedClosedImage = item.closedImage;
            if (processedClosedImage && processedClosedImage.startsWith('data:image')) {
                processedClosedImage = await uploadAsset(processedClosedImage, project.id);
            }
        
            const newItem = { ...item, photos: processedPhotos, closedImage: processedClosedImage };
            if (!montiertRef.current) {
                // Waehrend des Uploads verlassen: auf den aktuellen App-Stand,
                // nicht auf den eingefrorenen `reportRef`.
                const angewendet = spaetWeitergeben(r => ({ ...r, items: neuenEintragEinfuegen(r.items, newItem) }));
                if (!angewendet) throw new Error('Projekt nicht mehr offen. Eintrag nicht gespeichert.');
                return;
            }
            // Bestand NACH dem Upload lesen, nicht davor: der Assistent nimmt
            // waehrenddessen womoeglich schon den naechsten Mangel auf. Der Ref
            // traegt den Stand inklusive der Eintraege, die waehrend dieses Uploads
            // dazugekommen sind — daraus entsteht die laufende Nummer (die
            // Information bekommt weiterhin keine).
            const aktuell = reportRef.current;
            if (!aktuell) throw new Error('Bericht nicht geladen. Eintrag nicht gespeichert.');
            const newItems = neuenEintragEinfuegen(aktuell.items, newItem);
            const updatedReport = { ...aktuell, items: newItems };
            reportRef.current = updatedReport;
            setReport(updatedReport);
            onUpdate(updatedReport);
            // Assistent bleibt offen: „Nächster Mangel" / „Anderer Eintrag" / „Fertig";
            // Schließen läuft über onCancel.
        } finally {
            window.clearTimeout(obergrenze);
            loesen();
            uploadEnde();
        }
    };

    /**
     * Freimeldung aus dem Wizard: schliesst einen bestehenden Mangel — derselbe
     * Weg wie „Mangel erledigen" am Desktop (statusWechselAnwenden), damit
     * Datum, Nummernwechsel und Freimelde-Felder identisch entstehen. Das Foto
     * wird vorher in den Storage gehoben; Base64 im Blob war der Weg zu 4707.
     */
    const handleMobileWizardClose = async (id: string, felder: { statusComment: string; closedImage?: string }) => {
        if (!report) throw new Error('Bericht nicht geladen. Freimeldung nicht gespeichert.');
        if (isReadOnly) {
            alert(readOnlyMessage('Die Freimeldung wurde NICHT gespeichert.'));
            throw new Error('Nur Ansicht. Freimeldung nicht gespeichert.');
        }
        // Bis hier geschrieben ist, liegt der Eintrag nur in diesem Aufruf —
        // auch nach „Fertig“: kein Neuladen (Update-Balken, Deployment) und
        // kein Leerlauf-Rückzug dazwischen.
        const loesen = holdIdleExit(UPLOAD_HALTE.freimeldung);
        const obergrenze = window.setTimeout(loesen, UPLOAD_HALT_MS);
        // Senden/Verwerfen bleiben bis zum echten Ende gesperrt (uploadLaeuft).
        const uploadEnde = uploadBeginnt();
        try {
            let closedImage = felder.closedImage;
            if (closedImage && closedImage.startsWith('data:image')) {
                closedImage = await uploadAsset(closedImage, project.id);
            }
            if (!montiertRef.current) {
                const angewendet = spaetWeitergeben(r => ({
                    ...r,
                    items: statusWechselAnwenden(r.items, id, 'closed', r.metadata.date, {
                        closedDate: freimeldeDatum(r.metadata.date),
                        statusComment: felder.statusComment,
                        closedImage,
                    }),
                }));
                if (!angewendet) throw new Error('Projekt nicht mehr offen. Freimeldung nicht gespeichert.');
                return;
            }
            const aktuell = reportRef.current;
            if (!aktuell) throw new Error('Bericht nicht geladen. Freimeldung nicht gespeichert.');
            const reportDate = aktuell.metadata.date;
            const newItems = statusWechselAnwenden(aktuell.items, id, 'closed', reportDate, {
                closedDate: freimeldeDatum(reportDate),
                statusComment: felder.statusComment,
                closedImage,
            });
            const updatedReport = { ...aktuell, items: newItems };
            reportRef.current = updatedReport;
            setReport(updatedReport);
            onUpdate(updatedReport);
        } finally {
            window.clearTimeout(obergrenze);
            loesen();
            uploadEnde();
        }
    };

    /**
     * Wiederhergestellte Eintraege aufnehmen — MIT ihrer Nummernvergabe.
     *
     * Die Ansicht, aus der die Wiederherstellung kommt, hat vorher auf den
     * Archiv-Snapshot und auf die Rueckfrage gewartet; in dieser Zeit kann ein
     * mobiler Foto-Upload fertig geworden und ein Mangel dazugekommen sein.
     * Deshalb entscheidet die Nummer erst hier, gegen den Bestand aus dem Ref —
     * und das Einfuegen passiert im selben synchronen Zug, ohne `await`
     * dazwischen. Was vergeben wurde, geht zurueck an die Ansicht: genau diese
     * Eintraege schreibt sie in die Cloud, genau diese Wechsel meldet sie.
     */
    const uebernehmeWiederhergestellteEintraege = (wiederhergestellt: InspectionItem[]): EinordnungsErgebnis => {
        const aktuell = reportRef.current;
        // Unerreichbar, solange die Archivliste gerendert wird (der Bericht
        // steht dann) — der Rueckgabewert ist nur die ehrliche leere Antwort.
        if (!aktuell) return { eintraege: [], umnummeriert: [] };
        const ergebnis = eintraegeKollisionsfreiEinordnen(wiederhergestellt, aktuell.items);
        const updatedReport = { ...aktuell, items: [...aktuell.items, ...ergebnis.eintraege] };
        reportRef.current = updatedReport;
        setReport(updatedReport);
        onUpdate(updatedReport);
        return ergebnis;
    };

    const handleMergeReport = (importedReport: Report) => {
        // Aus dem Ref, nicht aus der Render-Closure: die Datei wurde vom Nutzer
        // ausgewaehlt und eingelesen, waehrenddessen kann ein Foto-Upload einen
        // Mangel nachgetragen haben. Der Closure-Stand kennte ihn nicht und
        // wuerde ihn beim Zusammenfuehren wieder herausschreiben.
        const aktuell = reportRef.current;
        if (!aktuell) return;
        const existingItemIds = new Set(aktuell.items.map(i => i.id));
        const newItems = importedReport.items.filter(i => !existingItemIds.has(i.id));
        // Der Import bringt die laufenden Nummern seines Berichts mit. Sind sie
        // hier inzwischen an einen anderen Sachverhalt vergeben, weicht der
        // Hereinkommende aus — der Bestand wird nie umnummeriert. Und der Nutzer
        // erfaehrt es: eine still geaenderte Nummer sucht er spaeter vergeblich.
        const { eintraege: eingeordnet, umnummeriert } = eintraegeKollisionsfreiEinordnen(newItems, aktuell.items);
        const mergedItems = [...aktuell.items, ...eingeordnet];
        const existingHazardIds = new Set((aktuell.mutualHazards || []).map(h => h.id));
        const newHazards = (importedReport.mutualHazards || []).filter(h => !existingHazardIds.has(h.id));
        const mergedHazards = [...(aktuell.mutualHazards || []), ...newHazards];
        const mergedChecklists = aktuell.checklists.map(existingC => {
            const importedC = importedReport.checklists.find(ic => ic.id === existingC.id || (ic.text === existingC.text && ic.category === existingC.category));
            if (importedC && (importedC.status || importedC.comment || importedC.lastChecked)) {
                return { 
                    ...existingC, 
                    status: importedC.status || existingC.status, 
                    comment: importedC.comment || existingC.comment, 
                    lastChecked: importedC.lastChecked || existingC.lastChecked,
                    isRelevant: importedC.isRelevant 
                };
            }
            return existingC;
        });
        const mergedReport = { ...aktuell, items: mergedItems, mutualHazards: mergedHazards, checklists: mergedChecklists };
        reportRef.current = mergedReport;
        setReport(mergedReport);
        onUpdate(mergedReport);
        if (umnummeriert.length) alert(beschreibeNummernWechsel(umnummeriert));
    };

    const handleFinishAndSend = async () => {
        // Sperre ganz am Anfang: der Ablauf bucht Leistungen (teils direkt im
        // Mutterprojekt) und verschickt eine E-Mail an den Kunden. Beides laeuft
        // an App.handleUpdateReport vorbei, der Lesemodus wuerde es nicht fangen.
        // Laeuft noch ein Upload (Assistent, Kartenfoto), fehlt sein Ergebnis im
        // Bericht — der Versand schriebe den Stand ohne es zurueck.
        const sperrgrund = isReadOnly
            ? readOnlyMessage('Es werden weder Leistungen gebucht noch Berichte versendet.')
            : uploadLaeuft()
                ? 'Fotos bzw. Einträge werden noch hochgeladen. Bitte einen Moment warten und dann erneut senden.'
                : null;
        if (sperrgrund) {
            alert(sperrgrund);
            return;
        }

        // VALIDATION: Check for incomplete OPEN items (Defects)
        // Mängel (Open) müssen Titel, Beschreibung, Ort und Firma haben.
        const incompleteDefects = report?.items.filter(i =>
            i.status === 'open' && (
                !i.title?.trim() ||
                !i.observation?.trim() ||
                !i.location?.trim() ||
                !i.responsiblePerson?.trim()
            )
        );

        if (incompleteDefects && incompleteDefects.length > 0) {
            alert(`Versand blockiert: Es gibt ${incompleteDefects.length} unvollständige Mängel (Status: Offen). Bitte überprüfen Sie Titel, Beschreibung, Ort und Verantwortliche Firma.`);
            onSectionChange('items'); // Navigate to items view to fix
            return;
        }

        // Ref-Lock: der State greift erst nach dem Re-Render, ein Doppelklick
        // startet sonst zwei parallele Prüfungen und zwei Versand-Abläufe.
        if (sendGuardRef.current) return;
        sendGuardRef.current = true;
        setIsCheckingSendGuard(true);
        try {
            // Freigabe-Sperre ZUERST: sie beantwortet die Frage „darf dieser
            // Bericht überhaupt hinaus?", der Schon-versendet-Check nur „ist er
            // schon einmal hinaus?". Schlägt die Abfrage fehl (Migration Nr. 21
            // noch nicht gelaufen, offline), blockiert sie bewusst nicht.
            const release = await checkReportRelease(project.id, {
                reportDate: report?.metadata.date,
            });
            if (release.blockiert) {
                setReleaseBlock(release);
                return;
            }

            // Schon-versendet-Check: das lokale isExported-Flag reicht nicht,
            // weil die Nummernerhöhung es zurücksetzt (siehe sendGuard.ts).
            // `istUebung` gehört in die Abfrage, sonst greift der
            // UEBUNG_BLOCK des Sendeschutzes auf diesem Weg NIE: er ist der
            // erste Fall in `evaluateSendGuard`, aber nur, wenn ihm jemand
            // sagt, dass ein Übungsprojekt offen ist. Die Projektnummer wird
            // dabei ausdrücklich mit übergeben — `istUebungsprojekt` sucht sie
            // unter `project_number`, der Bericht führt sie als
            // `metadata.projectNumber`.
            const decision = await checkAlreadySent(project.id, {
                inspectionNumber: report?.metadata.inspectionNumber ?? 0,
                sectionId: report?.metadata.sectionId,
                reportDate: report?.metadata.date,
                istUebung: istUebungsprojekt({
                    metadata: report?.metadata,
                    project_number: report?.metadata?.projectNumber,
                }),
            });
            if (decision.alreadySent) {
                setSendGuardWarning(decision);
                return;
            }
        } finally {
            sendGuardRef.current = false;
            setIsCheckingSendGuard(false);
        }

        void startSendFlow();
    };

    /** Versand-Ablauf ab der Abrechnung — nach bestandener bzw. bewusst übergangener Prüfung. */
    const startSendFlow = async () => {
        // Zweiter Riegel: startSendFlow wird auch aus dem "trotzdem senden"-Dialog
        // heraus aufgerufen. Kein Modal des Ablaufs darf im Lesemodus aufgehen.
        // Nicht still abbrechen: sonst bliebe die Warnung stehen und der
        // Senden-Knopf gesperrt, ohne dass jemand den Grund erfaehrt.
        if (isReadOnly) {
            abortSendFlow();
            alert(readOnlyMessage('Es werden weder Leistungen gebucht noch Berichte versendet.'));
            return;
        }
        setSendFlowActive(true);

        // WENN BEREITS EXPORTIERT: Überspringe Abrechnung, gehe direkt zum Versand
        if (report?.metadata.isExported) {
             setPrecapturedHours(1);
             setPrecapturedServiceId(null);
             setPrecapturedBookings([]);
             setSelectedAddons({});
             setShowEmailModal(true);
             return;
        }

        // --- CHECK IF ALREADY BOOKED VIA DAILY FLAT RATE ---
        // Rule: within one project group (parent + children) only ONE inspection
        // position may be billed per day — regardless of which LV position and
        // regardless of whether the parent or a child books first. Blocked
        // positions can still be forced via the confirm dialog in the modal.
        const config = report?.metadata.globalData?.linkedProjectConfig;
        const blocked: Record<string, { reason: string }> = {};
        const reportDate = report?.metadata.date || new Date().toISOString().split('T')[0];

        const describeBooker = (entry: any): string => {
            if (entry?.description) {
                 const match = entry.description.match(/"([^"]+)"|'([^']+)'/);
                 if (match) return `Projekt "${match[1] || match[2]}"`;
                 if (entry.description.includes(report?.metadata.projectName || '---')) {
                     return "diesem Projekt (bereits gebucht)";
                 }
                 return "dem Mutterprojekt/Gruppe";
            }
            return "einem anderen Projekt";
        };

        if (config && config.role === 'child' && parentProjectData) {
            const parentLogs = getServiceLog(parentProjectData);
            const availServices = parentServices.filter(s => s.assignment === 'inspection');
            const inspectionIds = new Set(availServices.map(s => s.id));

            // Alle Begehungsbuchungen dieses Tages im Protokoll der Mutter.
            // quantity > 0 laesst die reinen "Bereits aktiv"-Nullzeilen aussen vor.
            const sameDay = parentLogs.filter(log => isSameDay(log.date, reportDate) && inspectionIds.has(log.serviceId) && (log.quantity || 0) > 0 && !isAccountingEntry(log));

            // Die Art gilt fuer den BEGEHUNGSTAG, nicht fuer heute — eine
            // spaetere Umstellung darf einen alten Bericht nicht umdeuten.
            const modus = billingModeAt(config, reportDate);

            // NICHT jeder Eintrag des Tages sperrt diese Tochter.
            //
            // Bei Tagespauschale galt hier frueher schlicht der ERSTE Eintrag
            // des Tages. Hat eine Mutter mehrere Toechter, sperrte damit auch
            // die Buchung einer EINZELN abrechnenden Schwester — die ihre eigene
            // Leistung abrechnet und ueber diesen Tag nichts aussagt. Das war
            // derselbe Reihenfolge-Fehler wie zuvor auf der Mutter-Seite, nur
            // eine Ebene tiefer, und er erzeugte reinen Fehlalarm.
            //
            // Die Art steht am jeweiligen KIND, nicht an der Mutter, und wird
            // deshalb fuer die beteiligten Schwestern nachgeschlagen —
            // aufgeloest auf den Begehungstag. Geladen wird nur, was die
            // Entscheidung wirklich braucht: rechnet diese Tochter einzeln ab,
            // sperrt ohnehin nur ihre eigene Zweitbuchung.
            const schwesterIds = modus === EINZELN ? [] : sameDay
                .map(log => childIdFromReportRef((log as any).reportRef))
                .filter((id): id is string => !!id && id !== project.id);
            const modusJeSchwester: Map<string, LinkedBillingMode | undefined> = schwesterIds.length > 0
                ? await fetchChildBillingModes(schwesterIds, reportDate)
                : new Map();

            const bewertet = sameDay
                .map(log => ({
                    log,
                    grund: sperrtFremdeBuchungDieTochter(log as any, {
                        childId: project.id,
                        childName: report?.metadata.projectName,
                        modus,
                        modusJeSchwester,
                    }),
                }))
                .filter((x): x is { log: typeof sameDay[number]; grund: TochterSperrgrund } => x.grund !== null);

            // Die eigene Zweitbuchung nennt den Grund am genauesten; sie geht
            // deshalb vor, egal an welcher Stelle des Protokolls sie steht.
            const treffer = bewertet.find(x => x.grund === 'eigene') ?? bewertet[0];

            if (treffer) {
                const tagText = new Date(reportDate).toLocaleDateString();
                const reason = treffer.grund === 'eigene'
                    ? `Für dieses Projekt ist am ${tagText} bereits eine Begehung gebucht.`
                    : treffer.grund === 'schwester'
                        // Der Beleg liegt bei einem SCHWESTERprojekt — wer das
                        // nicht liest, sucht ihn bei der Mutter und findet nichts.
                        ? `Tagespauschale am ${tagText} bereits über ein Schwester-Projekt gebucht (${describeBooker(treffer.log)}).`
                        : `Tagespauschale am ${tagText} bereits über ${describeBooker(treffer.log)} gebucht.`;
                availServices.forEach(s => {
                    blocked[s.id] = { reason };
                });
            }
        } else if (!config || config.role !== 'child') {
            // Parent (or standalone) project: previously this path had NO same-day
            // check at all, so a parent inspection after a child booking produced
            // a second billed position on the same day. Check the OWN log.
            const ownLogs = getServiceLog(report);
            const ownInspectionServices = (report?.metadata.globalData?.services || []).filter(s => s.assignment === 'inspection');
            const inspectionIds = new Set(ownInspectionServices.map(s => s.id));

            const treffer = ownLogs.filter(log => isSameDay(log.date, reportDate) && inspectionIds.has(log.serviceId) && (log.quantity || 0) > 0 && !isAccountingEntry(log));

            // NICHT jede Buchung an diesem Tag sperrt die eigene Begehung.
            //
            // Eine Tochter schreibt ihre Buchung IMMER in das Protokoll der
            // Mutter — auch bei EINZELabrechnung, wo sie ihre eigene Leistung
            // hat und ueber die Begehung der Mutter nichts aussagt. Der Riegel
            // sperrte trotzdem und machte damit das Ergebnis von der
            // REIHENFOLGE abhaengig: Mutter zuerst, dann Tochter — beide
            // gebucht; Tochter zuerst, dann Mutter — Mutter gesperrt.
            //
            // Die Art steht am KIND, nicht an der Mutter, und wird deshalb hier
            // nachgeschlagen (aufgeloest auf den Begehungstag). Nur eine
            // TAGESPAUSCHALEN-Tochter sperrt; eine eigene Zweitbuchung der
            // Mutter sperrt weiterhin immer (dokumentierter Doppelbuchungsfall).
            const kindIds = treffer
                .map(log => childIdFromReportRef((log as any).reportRef))
                .filter((id): id is string => !!id);
            const modusJeKind: Map<string, LinkedBillingMode | undefined> = kindIds.length > 0
                ? await fetchChildBillingModes(kindIds, reportDate)
                : new Map();

            const entry = treffer.find(log => sperrtEigeneBegehung(log as any, modusJeKind));

            if (entry) {
                const booker = describeBooker(entry);
                ownInspectionServices.forEach(s => {
                    blocked[s.id] = {
                        reason: `Begehung am ${new Date(reportDate).toLocaleDateString()} bereits über ${booker} gebucht.`
                    };
                });
            }
        }
        
        setBlockedServices(blocked);
        setShowBillingModal(true);
    };

    /** Nutzer hat die "schon versendet"-Warnung gesehen und will bewusst erneut senden. */
    const handleConfirmRepeatSend = () => {
        // Zweiter Riegel neben dem ausgeblendeten Knopf: ein hart blockierter
        // Treffer (Übungsprojekt) kennt kein „trotzdem senden".
        if (sendGuardWarning?.hartBlockiert) { setSendGuardWarning(null); return; }
        // Vor dem Nullen auslesen: bei einem Altbestand-Treffer (abweichendes
        // Begehungsdatum im Archiv) bleibt der Versand ein Erstversand — kein
        // Wiederholungs-Vermerk im Sendeverlauf.
        const suppressPrefix = sendGuardWarning?.suppressRepeatPrefix === true;
        setSendGuardWarning(null);
        if (!suppressPrefix) setIsRepeatSend(true);
        void startSendFlow();
    };

    /** Abbruch an beliebiger Stelle des Ablaufs — gibt den Senden-Button wieder frei. */
    const abortSendFlow = () => {
        if (emailModalTimerRef.current) {
            clearTimeout(emailModalTimerRef.current);
            emailModalTimerRef.current = null;
        }
        setShowBillingModal(false);
        setShowAdditionalModal(false);
        setShowEmailModal(false);
        setSendGuardWarning(null);
        setSendFlowActive(false);
        setIsRepeatSend(false);
    };

    const handleConfirmMainBilling = async (serviceId: string | null, hours: number, rows: BookingSplitRow[] = []) => {
        // Letzter Riegel vor saveProjectToCloud: dieser Pfad schreibt Leistungen
        // und einen Protokolleintrag direkt ins verknuepfte Mutterprojekt.
        if (isReadOnly) {
            abortSendFlow();
            alert(readOnlyMessage('Es wurde nichts abgerechnet.'));
            return;
        }

        // --- PARENT / CHILD BILLING LOGIC ---
        const config = report?.metadata.globalData?.linkedProjectConfig;
        const isLinkedChild = config?.role === 'child';

        // Aufteilen ist im Kind→Mutter-Pfad NICHT unterstuetzt (der Dialog
        // bietet es dort erst gar nicht an, siehe allowSplit). Der Riegel steht
        // trotzdem hier: still nur die erste Zeile zu buchen waere der
        // schlimmste Ausgang — die uebrigen Lose fehlten unbemerkt auf der
        // Rechnung.
        if (isLinkedChild && rows.length > 1) {
            abortSendFlow();
            alert('Bei Abrechnung über das Mutterprojekt lässt sich eine Begehung nicht auf mehrere Lose aufteilen. Bitte die Lose einzeln abrechnen.');
            return;
        }

        // Eine Zeile bleibt eine Zeile: serviceId/hours sind dann exakt die
        // bisherigen Werte, die Liste haelt nur zusaetzlich die Mengen je Los.
        const bookings: BookingSplitRow[] = rows.length > 0
            ? rows
            : (serviceId ? [{ serviceId, quantity: hours }] : []);

        setShowBillingModal(false);
        setPrecapturedHours(hours);
        setPrecapturedServiceId(serviceId);
        setPrecapturedBookings(bookings);

        if (serviceId && config && config.role === 'child' && parentProjectData) {
            // We need to update the PARENT project
            try {
                // Reload parent fresh from cloud to avoid race conditions
                const freshParent = await loadProjectById(config.linkedProjectId);
                if (!freshParent) throw new Error("Parent not found");
                
                const parentMeta = freshParent.data.metadata as ReportMetadata;
                const parentServices = parentMeta.globalData?.services || [];
                const parentLogs = parentMeta.globalData?.serviceLog || [];

                // Idempotency: this child report must book on the parent only ONCE.
                // Without this, cancelling the e-mail step after billing and running
                // the flow again produced a second parent booking (the isExported
                // flag is only set after a successful send).
                const childReportRef = `RPT-CHILD-${project.id}-${report.metadata.inspectionNumber}`;
                const alreadyBookedForThisReport = parentLogs.some((l: any) => l.reportRef === childReportRef);

                const targetServiceIndex = parentServices.findIndex(s => s.id === serviceId);

                // PAUSCHALE DER MUTTER: bucht die Mutter mit Monatspauschale,
                // geht die Begehung der Tochter als NACHWEISZEILE auf deren
                // Pauschale — Menge 0, quantityUsed/status bleiben, der Monat
                // zaehlt allein ueber FLAT-JJJJ-MM. Das gilt fuer die Pauschale
                // selbst und jede Position, die sie DECKT (gemerkte 2.7.10 von
                // frueher); ein Los, das sie nicht deckt, bucht weiter seine
                // Menge samt Tagesriegel (flatTargetForInspectionChoice, dieselbe
                // Regel wie der Versand im eigenen Projekt).
                // Der Tagesriegel entfaellt fuer den Nachweis — eine
                // Nachweiszeile kostet nichts, und derselbe Ref-Riegel oben haelt
                // den Bericht einmalig.
                const pauschaleId = flatTargetForInspectionChoice(parentServices, serviceId);
                const pauschale = pauschaleId ? parentServices.find(x => x.id === pauschaleId) : undefined;

                if (pauschale && !alreadyBookedForThisReport) {
                    const reportDate = report?.metadata.date || new Date().toISOString().split('T')[0];
                    const nachweis = planFlatProofEntry({
                        id: crypto.randomUUID(),
                        flatServiceId: pauschale.id,
                        date: new Date(reportDate).toISOString(),
                        bookedAt: new Date().toISOString(),
                        performerId: userProfile?.id || 'system',
                        performerName: buildActorName(userProfile)
                            || realActorName(report?.metadata.conductedByName),
                        // Dieselbe Form wie das Nachbuchen (reportRebookService):
                        // KIND_HAUPTBUCHUNG erkennt den Anfang, die Klammer sagt,
                        // dass kein Mengenstand gebucht wurde.
                        description: childBookingDescription(report?.metadata.projectName || 'Tochter', report.metadata.inspectionNumber, 1, 'Nachweis'),
                        reportRef: childReportRef,
                    });
                    const updatedParent = {
                        ...freshParent.data,
                        metadata: {
                            ...parentMeta,
                            globalData: {
                                ...parentMeta.globalData,
                                services: parentServices,
                                serviceLog: [nachweis, ...parentLogs]
                            }
                        }
                    };
                    await saveProjectToCloud(updatedParent, config.linkedProjectId);
                    // Zweitschrift wie unten; die Aussage ist hier sicher: die
                    // Zeile belegt den Bericht der Tochter (RPT-CHILD-…).
                    void mirrorEntriesToJournal({
                        projectId: config.linkedProjectId,
                        entries: [nachweis],
                        previousLog: parentLogs,
                        source: 'child_to_parent',
                        linkedProjectConfig: parentMeta.globalData?.linkedProjectConfig ?? null,
                        statements: { [nachweis.id]: { isAddon: false, coversInspection: isReportProofEntry(nachweis) } },
                    });
                    setParentProjectData(updatedParent);
                    setParentServices(parentServices);
                } else if (targetServiceIndex !== -1 && !alreadyBookedForThisReport) {
                    const s = parentServices[targetServiceIndex];
                    let increment = 0;
                    let logMessage = '';
                    
                    const reportDate = report?.metadata.date || new Date().toISOString().split('T')[0];

                    // Die Art vom BEGEHUNGSTAG, nicht die heutige. Der Riegel in
                    // `startSendFlow` urteilt oben bereits nach
                    // `billingModeAt(config, reportDate)`; nahm die Buchung hier
                    // `config.billingMode`, sprachen zwei Stellen derselben Funktion
                    // ueber denselben Bericht mit verschiedenen Massstaeben. Eine
                    // Umstellung zwischen Begehungstag und Versand kippte damit Menge
                    // und Wortlaut gegen die Sperre, die den Fall gerade beurteilt hat.
                    const modusAmTag = billingModeAt(config, reportDate);

                    if (modusAmTag === TAGESPAUSCHALE) {
                        // Check if ANY billing happened on this day for inspection
                        // services — group-wide across all LV positions, so two
                        // children picking different positions still count as one
                        // daily flat. quantity > 0 skips "Bereits aktiv" zero-entries.
                        const inspectionIds = new Set(parentServices.filter(x => x.assignment === 'inspection').map(x => x.id));
                        // Lockstep mit unbookedReportsLogic.istTagGedeckt: auch eine
                        // Nachweiszeile mit Berichtsbezug (Monatspauschale der
                        // Mutter, Menge 0) deckt den Tag — sonst buchte der Versand
                        // hier, wo der Nachtlauf den Tag fuer gedeckt haelt.
                        const alreadyBilledToday = parentLogs.some(log => {
                            if (!isSameDay(log.date, reportDate)) return false;
                            if (isInspectionProofEntry(log, null)) return true;
                            return inspectionIds.has(log.serviceId) && (log.quantity || 0) > 0 && !isAccountingEntry(log);
                        });
                        
                        const isBlocked = blockedServices[serviceId] !== undefined;

                        if (alreadyBilledToday) {
                            if (isBlocked) {
                                // User forced it
                                increment = 1;
                                logMessage = `Tochter-Projekt "${report?.metadata.projectName}": Bericht Nr. ${report.metadata.inspectionNumber} (ERZWUNGEN)`;
                            } else {
                                increment = 0;
                                logMessage = `Tochter-Projekt "${report?.metadata.projectName}": Bericht Nr. ${report.metadata.inspectionNumber} (Bereits aktiv)`;
                            }
                        } else {
                            increment = 1; 
                            logMessage = `Tochter-Projekt "${report?.metadata.projectName}": Bericht Nr. ${report.metadata.inspectionNumber}`;
                        }
                    } else {
                        // Individual
                        increment = isHourlyUnit(s.unit) ? hours : 1;
                        logMessage = `Tochter-Projekt "${report?.metadata.projectName}": Bericht Nr. ${report.metadata.inspectionNumber} (${increment} ${s.unit})`;
                    }
                    
                    if (increment > 0 || modusAmTag === TAGESPAUSCHALE) {
                         const newUsed = (s.quantityUsed || 0) + increment;
                         const isDone = s.quantityContracted > 0 && newUsed >= s.quantityContracted;
                         
                         const updatedService = { ...s, quantityUsed: newUsed, status: isDone ? 'done' : s.status };
                         parentServices[targetServiceIndex] = updatedService as ServiceItem; 
                         
                         const newLogEntry = {
                             id: crypto.randomUUID(),
                             date: new Date(reportDate).toISOString(), // Use Report Date for correct Daily Flat Rate check
                             bookedAt: new Date().toISOString(),
                             serviceId: s.id,
                             quantity: increment,
                             performerId: userProfile?.id || 'system',
                             // Der Mensch, der die Begehung der Tochter
                             // versendet und damit auf die Mutter bucht — frueher
                             // stand hier der Stub 'System (Linked)'. Der ist
                             // truthy und verdraengte im Leistungsnachweis den
                             // Archivnamen bis in die Koordinatorenspalte des
                             // KUNDENdokuments (Projekt 6172). Faellt beides aus,
                             // bleibt der Name leer und der Archivabgleich des
                             // Nachweises setzt ihn: kein Name ist besser als ein
                             // erfundener.
                             performerName: buildActorName(userProfile)
                                 || realActorName(report?.metadata.conductedByName),
                             isSubstitution: false,
                             description: logMessage,
                             reportRef: childReportRef
                         };
                         
                         // Save Parent
                         const updatedParent = {
                             ...freshParent.data,
                             metadata: {
                                 ...parentMeta,
                                 globalData: {
                                     ...parentMeta.globalData,
                                     services: parentServices,
                                     serviceLog: [newLogEntry, ...parentLogs]
                                 }
                             }
                         };
                         
                         await saveProjectToCloud(updatedParent, config.linkedProjectId);

                         // Zweitschrift ins Buchungsjournal (S4b). Geschrieben
                         // wurde auf die MUTTER, also gehoeren Projekt-id,
                         // Protokollstand und Verknuepfung ihr — die Migration
                         // liest die Abrechnungsart ebenfalls am Projekt der
                         // ZEILE, mit der Verknuepfung der Tochter liefen beide
                         // Fassungen auseinander.
                         //
                         // `statements`: diese Stelle weiss, dass sie die
                         // HAUPTbuchung der Begehung schreibt und nie eine
                         // Zusatzleistung. Gedeckt ist die Begehung aber nur,
                         // wenn wirklich etwas gebucht wurde — die Zeile mit
                         // increment 0 ist der Vermerk "Bereits aktiv" der
                         // Tagespauschale und belegt nichts; gedeckt hat sie
                         // die Buchung der Mutter an diesem Tag.
                         //
                         // Ohne `await`: eine abgelehnte Zusage duerfte den
                         // catch dieses Blocks nie erreichen, der dem Anwender
                         // eine gescheiterte Abrechnung meldet.
                         void mirrorEntriesToJournal({
                             projectId: config.linkedProjectId,
                             entries: [newLogEntry],
                             previousLog: parentLogs,
                             source: 'child_to_parent',
                             linkedProjectConfig: parentMeta.globalData?.linkedProjectConfig ?? null,
                             statements: { [newLogEntry.id]: { isAddon: false, coversInspection: increment > 0 } },
                         });

                         // Refresh local parent data
                         setParentProjectData(updatedParent);
                         setParentServices(parentServices);
                    }
                }
            } catch (e) {
                console.error("Error billing parent project", e);
                alert("Fehler bei der Abrechnung über das Mutterprojekt.");
            }
        }
        
        if (report) {
            // Check if project has additional services (Monatspositionen und
            // Pauschalen sind keine — siehe additionalServiceCandidates)
            const addons = additionalServiceCandidates(report.metadata.globalData?.services);

            if (addons.length > 0) {
                setShowAdditionalModal(true);
            } else {
                // Ohne Dialog keine Zusatzleistungen — auch keine aus einem
                // frueheren Versand in dieser Sitzung: EmailModal bucht, was
                // in selectedAddons steht.
                setSelectedAddons({});
                // bookings lokal weiterreichen: der State oben ist in diesem
                // Durchlauf noch nicht uebernommen.
                handleCompleteFlow(serviceId, hours, {}, bookings);
            }
        }
    };

    const handleConfirmAdditional = (addons: Record<string, number>) => {
        // Auch die spaeteren Schritte des Ablaufs bekommen einen eigenen Riegel:
        // der erste Riegel schuetzt nur den Einstieg, hier haengt am Ende der
        // Versand an den Kunden.
        if (isReadOnly) {
            abortSendFlow();
            alert(readOnlyMessage('Es wurde nichts abgerechnet und nichts versendet.'));
            return;
        }
        setShowAdditionalModal(false);
        setSelectedAddons(addons);
        handleCompleteFlow(precapturedServiceId, precapturedHours, addons, precapturedBookings);
    };

    const handleCompleteFlow = (serviceId: string | null, _hours: number, _addons: Record<string, number>, bookings: BookingSplitRow[] = []) => {
        // Letzte Station vor dem E-Mail-Fenster — hier faellt die Entscheidung,
        // ob der Versand ueberhaupt angeboten wird.
        if (isReadOnly) {
            abortSendFlow();
            alert(readOnlyMessage('Es wurde nichts abgerechnet und nichts versendet.'));
            return;
        }
        if (report) {
            const updatedReport = {
                ...report,
                metadata: {
                    ...report.metadata,
                    currentBillingServiceId: serviceId || undefined,
                    // Nur bei echter Aufteilung schreiben: ohne sie bleibt die
                    // Metadaten-Struktur eines Berichts unveraendert, und jeder
                    // Leser faellt wie bisher auf currentBillingServiceId zurueck.
                    currentBillingSplit: bookings.length > 1 ? bookings : undefined
                }
            };
            setReport(updatedReport);
            onUpdate(updatedReport);
            // Der Timer gehoert in eine Ref: ohne sie oeffnet sich das
            // E-Mail-Fenster noch nach einem Abbruch (oder nach dem Verlassen
            // des Moduls) — abortSendFlow raeumt sonst gegen einen Timer auf,
            // den es nicht kennt.
            if (emailModalTimerRef.current) clearTimeout(emailModalTimerRef.current);
            emailModalTimerRef.current = setTimeout(() => {
                emailModalTimerRef.current = null;
                setShowEmailModal(true);
            }, 100);
        }
    };

    const availableInspectionServices = useMemo(() => {
        if (!report) return [];
        
        // NEP-Begehungspositionen werden hier NICHT angeboten: der Versand
        // bucht ohne Begruendung, und die Vorauswahl geht als
        // precapturedServiceId ungefiltert in die Buchung. Nachbeauftragte
        // Begehungen bucht der Buchungsdialog im Projekt-Hub mit Grund
        // (dieselbe Regel wie selectBillableServices).
        // IF LINKED CHILD, USE PARENT SERVICES
        const pool = report.metadata.globalData?.linkedProjectConfig?.role === 'child'
            ? parentServices
            : (report.metadata.globalData?.services || []);

        // Pauschalprojekt: die Pauschale und daneben nur die
        // Begehungspositionen, die sie NICHT deckt (zweites Los) — dieselbe
        // Regel wie selectBillableServices. Stuende eine gedeckte 2.7.10
        // daneben, holte die Vorauswahl (last_billing_inspection) sie zurueck,
        // und der Versand buchte am Nachweis vorbei; eine gemerkte Position,
        // die hier fehlt, verwirft pickInitialBillingIds. Ohne Pauschale wie
        // bisher: Zuordnung 'inspection' ohne NEP.
        return selectInspectionSendServices(pool);
    }, [report, parentServices]);

    // Vorauswahl im Buchungsdialog aus dem Protokoll: wohin hat DIESES Projekt
    // seine letzte Begehung gebucht? Eine Tochter liest ihre Kind-Zeilen im
    // Protokoll der Mutter, nie die Wahl der Mutter (6290-2 #22/#23).
    const preferredInspectionIds = useMemo(() => {
        if (!report) return [];
        const isChild = report.metadata.globalData?.linkedProjectConfig?.role === 'child';
        if (isChild && !parentProjectData) return [];
        return preferredInspectionServiceIds(
            availableInspectionServices,
            isChild ? getServiceLog(parentProjectData!) : getServiceLog(report),
            isChild ? project.id : null,
        );
    }, [report, parentProjectData, availableInspectionServices, project.id]);

    const additionalServices = useMemo(() => {
        if (!report) return [];
        // Child projects typically don't have own services, but if they do, use them
        return additionalServiceCandidates(report.metadata.globalData?.services);
    }, [report]);

    // Ein einziger Sperr-Ausdruck statt eines Locks pro Schritt: kein Fenster
    // zwischen zwei Modals, in dem der Button kurz wieder scharf wäre.
    const isSendFlowBusy = isCheckingSendGuard || sendFlowActive || !!sendGuardWarning || !!releaseBlock;

    // InspectionList und SafetyPointsView laden Bilder selbst in den Storage,
    // bevor sie den Save-Pfad anstossen. Ohne projectId fallen sie auf das
    // Base64-Bild zurueck: im Lesemodus entstehen so keine Waisen im Bucket.
    //
    // Steht hier oben und nicht mehr unter renderContent, weil useFreimeldungen
    // dieselbe Id braucht — und ein Hook NIE hinter einem fruehen Return stehen
    // darf (Ladezustand, Mobil-Wizard).
    const uploadProjectId = isReadOnly ? undefined : project.id;

    // Nur-Ansicht sieht Freimeldungen (Abzeichen, Hinweis, Panel), darf sie
    // nur nicht bearbeiten — deshalb eigene Id ohne den Lesemodus-Schutz von
    // uploadProjectId; leer/'temp-id' faengt useFreimeldungen selbst ab.
    const freimeldungProjectId = project.id;

    // Externe Freimeldungen des Projekts (docs/freimeldung-online-plan.md §F).
    // Fehlt Migration 108/109, bleiben die Listen leer — Karten, Abzeichen und
    // Panel verschwinden dann rueckstandslos.
    const {
        offeneJeItem: freimeldungenJeItem,
        freimeldungen: alleFreimeldungen,
        offene: offeneFreimeldungen,
        links: freimeldungLinks,
        laedt: freimeldungenLaden,
        fehler: freimeldungFehler,
        entscheidungsFehler: freimeldungEntscheidungsFehler,
        entscheidungsHinweisSetzen: freimeldungHinweisSetzen,
        reload: freimeldungenNeuLaden,
        entscheiden: freimeldungEntscheiden,
    } = useFreimeldungen(freimeldungProjectId);

    /**
     * Verwaiste Meldungen — HIER OBEN, nicht unten im Panel.
     *
     * Gerechnet wird ueber alle Meldungen und alle Maengel; unter
     * `renderContent` lief das bei jedem Tastendruck in der Liste erneut. Ein
     * `useMemo` an der Verwendungsstelle ginge NICHT: `renderContent` steht
     * hinter den fruehen Returns (Ladezustand, Mobil-Wizard), ein Hook dort
     * verschoebe die Hook-Reihenfolge und riss die Maske beim naechsten Render
     * auseinander. Deshalb oben, mit `report?.items ?? []` — hier ist `report`
     * noch `Report | null`.
     */
    const verwaisteFreimeldungen = useMemo(
        () => verwaiste(alleFreimeldungen, report?.items ?? []),
        [alleFreimeldungen, report?.items],
    );

    /**
     * ERST DER NACHWEIS, DANN DIE ENTSCHEIDUNG
     * (docs/freimeldung-online-plan.md §F).
     *
     * `report_freimeldung_entscheiden` fasst `inspection_items` nicht an: der
     * Mangel wird in der Liste geschlossen und erst vom entprellten Autosave in
     * App.tsx hochgeladen. Wuerde die Meldung sofort gebucht, stuende sie nach
     * einem gescheiterten Save auf „bestaetigt", waehrend der Mangel auf dem
     * Server weiter offen ist — der Koordinator bekaeme den Hinweis nie wieder
     * zu sehen, und die Begruendung fuer die Erledigung waere fort.
     *
     * ENTSCHIEDEN WIRD DESHALB ERST, WENN DER MANGEL SERVERSEITIG NICHT MEHR
     * OFFEN IST — kein Signal, sondern ein Nachweis. Frueher genuegte hier der
     * Sprung von `saveStatus` auf 'saved'. Dieses Signal gilt aber fuer den
     * ganzen Speicherlauf und nicht fuer unser Projekt: useOfflineSync
     * speichert mit `flushAll` auch fremde Projekte, und deren geglueckter Save
     * haette eine Entscheidung bestaetigt, die nie irgendwo angekommen ist.
     * Gefragt wird jetzt `mangelServerStatus(project.id, itemId)` — eine
     * Lesefrage an genau diese Zeile —, und `nachweisErlaubtBuchung` macht
     * daraus das Ja/Nein.
     *
     * DIE WARTESCHLANGE LIEGT NUR IM SPEICHER. Ein Neuladen wirft sie weg, die
     * Meldung bleibt „offen" und das Abzeichen steht weiter an der Karte. Das
     * ist der sichere Ausgang — der Koordinator entscheidet noch einmal.
     *
     * ═══ DREI WEGE HINEIN, EINER DARAN VORBEI ═══════════════════════════════
     *
     * Alle Bestaetigungen gehen ueber diese Schlange und damit ueber den
     * Nachweis:
     *
     *   (1) DER FREIMELDE-DIALOG an der Mangelkarte (InspectionList,
     *       `confirmCloseItem`): schliesst den Mangel im Client, der Save
     *       laeuft entprellt hinterher → Nachweis `closed`.
     *   (2) „ZUR KENNTNIS GENOMMEN" an einem bereits erledigten Mangel
     *       (InspectionItemCard): auch dessen Erledigung kann rein lokal sein
     *       und noch in keinem Save stecken → Nachweis `closed`.
     *   (3) EINE INFORMATION ODER EINE VERWAISTE MELDUNG — der nachtraeglich
     *       auf `general` gestellte Mangel (InspectionList,
     *       `openCloseFromFreimeldung`) und die Meldung ohne Mangel im Panel
     *       (`FreimeldungPanel`, „Erledigt"). Beide erreichen `closed` NIE;
     *       der Nachweis lautet hier `sonstig` oder `fehlt`. Sie buchten
     *       frueher SOFORT und verliessen sich damit auf den lokalen Stand,
     *       waehrend der Save noch ausstand — scheiterte er, stand die Meldung
     *       auf „bestaetigt" (und riss ueber die RPC die Geschwister
     *       desselben Mangels mit), der Mangel serverseitig weiter offen.
     *
     * NUR „ABLEHNEN" LAEUFT SOFORT: dort wird nichts geschlossen, es gibt also
     * auch nichts nachzuweisen — und der sichere Ausgang liegt ohnehin auf
     * derselben Seite (die Meldung ist erledigt, der Mangel bleibt offen).
     */
    const freimeldungWarteschlange = useRef<FreimeldungEntscheidungAuftrag[]>([]);
    // Verhindert, dass zwei Ausloeser (Speichersignal und Nachfass-Uhr)
    // dieselbe Meldung gleichzeitig pruefen und doppelt buchen.
    const freimeldungFlushLaeuft = useRef(false);
    // Nur zum Ein- und Ausschalten der Nachfass-Uhr: ein Ref allein wuerde den
    // Effekt nicht wecken.
    const [freimeldungWartend, setFreimeldungWartend] = useState(0);

    /**
     * DREI KENNZEICHEN, DIE EINEN LAUFENDEN FLUSH UNGUELTIG MACHEN.
     *
     * Zwischen Lesefrage und Buchung liegt ein `await`, und in dieser Luecke
     * kann sich alles aendern, was den Flush ueberhaupt erlaubt:
     *
     *   • DIE ANSICHT IST FORT (Zurueck, Projektwechsel, Abmelden). Ein Flush
     *     hinter einem `await` feuerte den Entscheidungs-RPC trotzdem ab —
     *     eine Buchung fuer eine Maske, die niemand mehr sieht, und der
     *     Klartext eines Fehlers landete in einem Zustand, den es nicht mehr
     *     gibt.
     *   • DIE PROJEKTSPERRE IST GEKIPPT (Admin bricht auf, eigener Herzschlag
     *     bleibt aus → Nur-Ansicht). Wer nicht mehr schreiben darf, darf auch
     *     nicht mehr entscheiden; sonst bestaetigt die Uhr im Hintergrund
     *     weiter, waehrend jeder Save des Moduls still verworfen wird.
     *   • DAS PROJEKT IST EIN ANDERES. Die Schlange gehoert dem Projekt, fuer
     *     das sie gefuellt wurde; eine Lesefrage an das neue Projekt beweist
     *     nichts ueber den alten Mangel.
     *
     * Die Kennzeichen liegen in Refs, weil der Flush sie MITTEN IM LAUF liest
     * und nicht beim Erzeugen seiner Closure. `isReadOnly` und `project.id`
     * werden beim Rendern nachgezogen und nicht im Effekt (Muster `reportRef`
     * oben): ein Effekt laeuft erst nach dem Anstrich, und genau in dieser
     * Luecke buchte die Uhr noch einmal.
     *
     * DIE SCHLANGE UEBERLEBT EINE NUR-ANSICHT-PHASE. Sie wird nicht geleert,
     * nur nicht abgearbeitet; wird das Projekt wieder schreibbar, laeuft die
     * Uhr weiter und holt die Buchung nach. Sie liegt aber NUR IM SPEICHER —
     * ein Neuladen der Seite wirft sie weg, die Meldung bleibt „offen" und der
     * Koordinator entscheidet sie noch einmal. Das ist der sichere Ausgang.
     */
    const freimeldungMontiert = useRef(true);
    useEffect(() => {
        freimeldungMontiert.current = true;
        return () => { freimeldungMontiert.current = false; };
    }, []);
    const freimeldungNurAnsicht = useRef(isReadOnly);
    freimeldungNurAnsicht.current = isReadOnly;
    const freimeldungProjektJetzt = useRef<string>(project.id);
    freimeldungProjektJetzt.current = project.id;

    const freimeldungWarteschlangeSetzen = useCallback((q: FreimeldungEntscheidungAuftrag[]) => {
        freimeldungWarteschlange.current = q;
        setFreimeldungWartend(q.length);
    }, []);

    /**
     * Der Klartext fuer einen Fehlschlag, den der RPC NIE gesehen hat: der
     * Nachweis blieb nach FREIMELDUNG_MAX_NACHWEISE Versuchen aus, oder der
     * Auftrag war gar nicht erst einzureihen. `entscheiden` im Hook kann davon
     * nichts melden — es lief nicht —, und ohne diesen Weg bliebe die Sache
     * still: der Koordinator klickte „Bestätigen", und die Karte saehe
     * hinterher aus wie vorher.
     *
     * Die Berichtssprache kommt aus `reportRef` und nicht aus `report`: der
     * Abarbeiter liest sie MITTEN IM LAUF, und als Abhaengigkeit haenge sonst
     * die ganze Kette (Flush, Einreihen, Nachfass-Uhr) an jedem Tastendruck im
     * Bericht.
     */
    const freimeldungNachweisGescheitert = useCallback(() => {
        const en = reportRef.current?.metadata?.reportLanguage === 'en';
        freimeldungHinweisSetzen(freimeldungNachweisHinweis(en ? 'en' : 'de'));
    }, [freimeldungHinweisSetzen]);

    /**
     * Arbeitet die Warteschlange ab: fuer jeden Auftrag erst die Lesefrage,
     * dann — und nur bei einem klaren „ja" — die Buchung. Gebucht wird bei
     * `closed`, `sonstig` (der Mangel ist als Aufgabe fort) und `fehlt` (die
     * Zeile gibt es in diesem Projekt nicht mehr); `open` (steht noch offen)
     * und `null` (nicht feststellbar: kein Netz, RLS, fehlende Tabelle)
     * bleiben beide in der Schlange (`nachweisErlaubtBuchung`).
     *
     * GESTRICHEN WIRD NUR, WAS GEBUCHT IST. `freimeldungEntscheiden` faengt
     * seine Fehler ab und laeuft danach normal weiter; frueher strich der
     * Flush den Auftrag trotzdem. Ein gescheiterter RPC — kein Netz, RLS,
     * abgelaufene Sitzung — sah damit aus wie eine Buchung: der Auftrag war
     * fort, die Schlange leer, die Nachfass-Uhr hielt an (sie laeuft nur,
     * solange etwas wartet), und die Entscheidung des Koordinators war
     * verloren, ohne je in der Datenbank angekommen zu sein. Jetzt zaehlt das
     * ausdrueckliche `ok` des Hooks.
     *
     * ═══ UND DER KEIL IN DIE ANDERE RICHTUNG ════════════════════════════════
     *
     * „Bleibt stehen, wird erneut versucht" ist fuer ein abgerissenes Netz
     * richtig und fuer vier Antworten der Datenbank falsch: die Meldung wurde
     * inzwischen anders entschieden (P0001), es gibt sie nicht mehr (P0002),
     * die Eingabe ist abgelehnt (22023), RLS verweigert (42501). Diese
     * Auftraege koennen NIE durchgehen; sie blieben fuer die ganze Sitzung in
     * der Schlange, und die 30-Sekunden-Uhr fragte bis zum Neuladen der Seite
     * erfolglos nach — unbegrenzt und, weil der Versuchszaehler nur
     * ausgebliebene NACHWEISE zaehlt, auch unbemerkt. Der Hook meldet sie als
     * `endgueltig` (`istEndgueltigerEntscheidungsfehler`): der Auftrag fliegt
     * raus, und der Koordinator bekommt den Grund im Klartext zu sehen, statt
     * dass seine Entscheidung still im Kreis laeuft.
     *
     * ═══ EIN HINWEIS JE LAUF, AM ENDE ═══════════════════════════════════════
     *
     * Der Hook loescht `entscheidungsFehler` NICHT mehr von sich aus, wenn
     * eine Entscheidung glueckt — in EINEM Lauf folgen mehrere aufeinander,
     * und ein Erfolg hinter einem Fehlschlag riss dessen Hinweis weg, bevor
     * ihn jemand lesen konnte. Zusammengefasst wird deshalb hier, einmal, im
     * `finally`, und zwar mit JEDEM zutreffenden Befund statt nur dem
     * dringlichsten (`freimeldungLaufHinweis`: endgueltig, aufgegeben,
     * wiederholbar) — sonst verschwieg ein wiederholbarer Fehlschlag, dass
     * daneben ein Auftrag endgueltig aufgegeben wurde. Nur ein Lauf, in dem
     * wirklich alles durchging, raeumt den Hinweis weg.
     *
     * BEIDE AWAITS SIND GEDECKELT (je 8 s, `FREIMELDUNG_ZEITBUDGET_MS` im
     * Service): `mangelServerStatus` gibt danach `null` (nicht feststellbar,
     * zaehlt als Versuch), die Buchung wirft. Ohne diese Uhren bliebe
     * `freimeldungFlushLaeuft` bei EINER haengenden Abfrage fuer die ganze
     * Sitzung stehen — jeder weitere Anlauf kehrte sofort um, und die Schlange
     * kaeme nie wieder in Gang.
     */
    const freimeldungWarteschlangeAbarbeiten = useCallback(async () => {
        if (freimeldungFlushLaeuft.current) return;
        // Gar nicht erst anfangen: demontiert oder Nur-Ansicht.
        if (!freimeldungMontiert.current || freimeldungNurAnsicht.current) return;
        const auftraege = freimeldungWarteschlange.current;
        if (auftraege.length === 0) return;
        // Das Projekt, fuer das dieser Lauf gilt — einmal festgehalten, damit
        // ein Wechsel mitten im Lauf auffaellt.
        const projektBeimStart: string = freimeldungProjektJetzt.current;
        // Nach JEDEM `await` neu gefragt. Ein Abbruch laesst die noch nicht
        // gebuchten Auftraege stehen.
        const abgebrochen = () =>
            !freimeldungMontiert.current
            || freimeldungNurAnsicht.current
            || freimeldungProjektJetzt.current !== projektBeimStart;
        freimeldungFlushLaeuft.current = true;
        const gebucht = new Set<string>();
        // Auftraege eines ANDEREN Projekts: weg, ohne sie je zu versuchen.
        const fremd = new Set<string>();
        // Endgueltig gescheitert (P0001, P0002, 22023, 42501): ebenfalls weg —
        // ein weiterer Versuch aendert daran nichts.
        const endgueltig = new Set<string>();
        let endgueltigKlartext: string | null = null;
        // Wiederholbar gescheitert (kein Netz, Zeitueberschreitung): bleibt in
        // der Schlange, sein Klartext bleibt am Ende des Laufs stehen.
        let wiederholbarKlartext: string | null = null;
        // Vergeblich geprueft: zaehlt einen Versuch hoch und faellt an der
        // Kappung heraus (`warteschlangeVersuchZaehlen`). Die Laufnummer kommt
        // mit, damit der Versuch nur gegen DENSELBEN Auftrag zaehlt und nicht
        // gegen eine Entscheidung, die der Koordinator waehrenddessen neu
        // eingereiht hat.
        const ohneNachweis: { id: string; laufnummer?: number }[] = [];
        try {
            // Nacheinander, nicht parallel: `entscheiden` laedt nach jedem
            // Aufruf nach, und `bestaetigt` erledigt ohnehin alle Meldungen
            // desselben Mangels mit.
            for (const auftrag of auftraege) {
                if (abgebrochen()) break;
                // DIE SCHLANGE GEHOERT DEM PROJEKT, FUER DAS SIE GEFUELLT
                // WURDE. Sie liegt im Speicher und ueberdauert einen Wechsel;
                // ein Auftrag des vorigen Projekts wuerde hier sonst gegen das
                // NEUE geprueft — die Lesefrage bewiese nichts ueber den alten
                // Mangel, im schlimmsten Fall traefe sie dort dieselbe Id.
                // Verworfen statt liegengelassen: die Meldung bleibt „offen",
                // und im richtigen Projekt entscheidet der Koordinator neu.
                if ((auftrag.projectId || '') !== projektBeimStart) {
                    console.warn('Freimelde-Auftrag gehört zu einem anderen Projekt und wird verworfen.', {
                        auftrag: auftrag.id,
                        erwartet: projektBeimStart,
                        gefunden: auftrag.projectId,
                    });
                    fremd.add(auftrag.id);
                    continue;
                }
                // Nur eine Bestaetigung haengt am Mangel. Ein „abgelehnt" in
                // der Schlange (heute reiht es niemand ein) wuerde sonst auf
                // ein Schliessen warten, das nie kommt.
                const serverStatus = auftrag.entscheidung === 'bestaetigt'
                    ? await mangelServerStatus(projektBeimStart, auftrag.itemId)
                    : null;
                if (abgebrochen()) break;
                const nachweis = auftrag.entscheidung === 'bestaetigt'
                    ? nachweisErlaubtBuchung(serverStatus)
                    : true;
                if (!nachweis) {
                    // Kein Beweis: `open` (Mangel steht noch offen) und `null`
                    // (nicht feststellbar) zaehlen beide als Versuch.
                    ohneNachweis.push({ id: auftrag.id, laufnummer: auftrag.laufnummer });
                    continue;
                }
                const ergebnis = await freimeldungEntscheiden(auftrag.id, auftrag.entscheidung, auftrag.kommentar);
                // Der RPC ist gelaufen — vermerkt wird er auch dann, wenn die
                // Ansicht inzwischen fort ist; sonst buchte ihn der naechste
                // Lauf ein zweites Mal.
                // `=== true`, nicht bloss wahrheitsgemaess: ohne
                // strictNullChecks grenzt TypeScript die Vereinigung sonst
                // nicht ein, und `endgueltig` waere im else-Zweig unbekannt.
                if (ergebnis.ok === true) {
                    gebucht.add(auftrag.id);
                } else if (ergebnis.endgueltig) {
                    // Nicht noch einmal versuchen: der Auftrag kaeme nie durch.
                    // Gefahrlos, weil nichts gebucht ist — die Meldung steht
                    // weiter „offen" (oder wurde anderswo entschieden), und der
                    // Koordinator liest gleich, warum.
                    console.warn('Freimelde-Entscheidung endgültig nicht buchbar, Auftrag verworfen.', {
                        auftrag: auftrag.id,
                        code: ergebnis.code,
                    });
                    endgueltig.add(auftrag.id);
                    endgueltigKlartext = ergebnis.klartext;
                } else {
                    wiederholbarKlartext = ergebnis.klartext;
                }
                if (abgebrochen()) break;
            }
        } finally {
            // DIESE EINE ZEILE LAEUFT IMMER, auch nach einem Abbruch: ein
            // stehengebliebenes Flag legte die Schlange fuer die ganze Sitzung
            // still, weil jeder weitere Anlauf oben sofort umkehrte.
            freimeldungFlushLaeuft.current = false;
            // ═══ EIN ABGEBROCHENER LAUF RAEUMT NICHTS MEHR AUF ══════════════
            //
            // Frueher folgte hier alles Weitere bedingungslos — obwohl die
            // Schleife bei `abgebrochen()` genau deshalb ausgestiegen war.
            // Nach einem Projektwechsel (das Modul bleibt dabei montiert)
            // erschien der Befund aus Projekt A in Projekt B oder loeschte
            // dort den Hinweis von B; nach dem Demontieren liefen die Setter
            // in eine Ansicht, die es nicht mehr gab.
            //
            //   • NICHT abgebrochen  → alles wie bisher.
            //   • PROJEKTWECHSEL (noch montiert) → nur die jetzt fremden
            //     Auftraege raus; kein Hinweis, der gehoerte zum alten
            //     Projekt.
            //   • DEMONTIERT oder NUR-ANSICHT → gar nichts. Die Schlange
            //     bleibt stehen, die Nachfass-Uhr holt sie nach, sobald wieder
            //     geschrieben werden darf.
            const abbruch = abgebrochen();
            if (!abbruch) {
                // Gegen den AKTUELLEN Stand gerechnet, nicht gegen die
                // Momentaufnahme von oben: waehrend des Abarbeitens kann eine
                // weitere Entscheidung eingereiht worden sein, und die darf weder
                // weggeraeumt werden noch einen fremden Versuch erben.
                const vorher = freimeldungWarteschlange.current;
                let naechste = vorher;
                if (gebucht.size > 0) naechste = warteschlangeBereinigen(naechste, gebucht);
                if (endgueltig.size > 0) naechste = warteschlangeBereinigen(naechste, endgueltig);
                if (fremd.size > 0) naechste = warteschlangeBereinigen(naechste, fremd);
                let aufgegeben = false;
                for (const { id, laufnummer } of ohneNachweis) {
                    const schritt = warteschlangeVersuchZaehlen(naechste, id, laufnummer);
                    naechste = schritt.warteschlange;
                    aufgegeben = aufgegeben || schritt.aufgegeben;
                }
                if (naechste !== vorher) freimeldungWarteschlangeSetzen(naechste);
                // ═══ EIN HINWEIS JE LAUF, ABER MIT ALLEN BEFUNDEN ══════════
                //
                // Erst jetzt, und nur einmal: der Koordinator soll nicht fuer
                // jeden Auftrag denselben Satz neu sehen.
                //
                // FRUEHER GEWANN EIN EINZIGER BEFUND (endgueltig vor
                // wiederholbar vor aufgegeben) — und daran ging der wichtigste
                // Satz verloren: Faellt Auftrag A an der Kappung aus der
                // Schlange, waehrend B nur am Netz haengt, stand da allein
                // „… Die App versucht es erneut." Der Koordinator hielt beide
                // fuer unterwegs, obwohl A NIE wieder versucht wird — genau
                // das, wovor `freimeldungNachweisHinweis` ihn bewahren soll.
                // `freimeldungLaufHinweis` setzt deshalb ALLE zutreffenden
                // Teile zusammen (freimeldungLogic.ts); dort haengt auch die
                // Zusage „Die App versucht es erneut." am wiederholbaren
                // Klartext, die nur die Warteschlange hier einloesen kann.
                //
                // STEHEN LASSEN, NICHT LOESCHEN: der Hook raeumt nach einem
                // Erfolg nichts mehr weg, und ein Fehlschlag frueher im selben
                // Lauf soll sichtbar bleiben. NUR ein rundum sauberer Lauf —
                // alles, was angefasst wurde, ist gebucht — raeumt den Hinweis
                // weg. Steht noch etwas in der Schlange oder wurde etwas
                // verworfen, gehoert der letzte Klartext weiter zur Lage.
                const en = reportRef.current?.metadata?.reportLanguage === 'en';
                const hinweis = freimeldungLaufHinweis({
                    endgueltig: endgueltigKlartext,
                    aufgegeben,
                    wiederholbar: wiederholbarKlartext,
                    lang: en ? 'en' : 'de',
                });
                if (hinweis) {
                    freimeldungHinweisSetzen(hinweis);
                } else if (gebucht.size > 0 && ohneNachweis.length === 0 && fremd.size === 0) {
                    freimeldungHinweisSetzen('');
                }
            } else if (freimeldungMontiert.current && freimeldungProjektJetzt.current !== projektBeimStart) {
                // PROJEKTWECHSEL MITTEN IM LAUF: Die Schlange liegt im
                // Speicher und ueberdauert den Wechsel. Was zum alten Projekt
                // gehoert, ist hier fremd — die Lesefrage traefe das falsche
                // Projekt, im schlimmsten Fall dieselbe Id. Verworfen statt
                // liegengelassen: die Meldung bleibt „offen", und im richtigen
                // Projekt entscheidet der Koordinator neu. Der Hinweis bleibt
                // unberuehrt; er gehoert dem neuen Projekt.
                const vorher = freimeldungWarteschlange.current;
                const jetzt = freimeldungProjektJetzt.current;
                const fremdJetzt = new Set<string>(
                    vorher.filter((a) => !!a && (a.projectId || '') !== jetzt).map((a) => a.id),
                );
                if (fremdJetzt.size > 0) {
                    console.warn('Projektwechsel während des Freimelde-Laufs, fremde Aufträge verworfen.', {
                        anzahl: fremdJetzt.size,
                        projekt: jetzt,
                    });
                    freimeldungWarteschlangeSetzen(warteschlangeBereinigen(vorher, fremdJetzt));
                }
            }
        }
    }, [
        freimeldungEntscheiden,
        freimeldungWarteschlangeSetzen,
        freimeldungHinweisSetzen,
    ]);

    /**
     * Dieselbe Entscheidung fuer die Oberflaeche, nur ohne Ergebnis.
     *
     * Panel und Mangelkarte erwarten `Promise<void>`: sie zeigen einen Fehler
     * ueber `entscheidungsFehler` an und haben fuer einen Befund keine
     * Verwendung. Das ausdrueckliche Ergebnis braucht allein die Warteschlange
     * oben, die sonst einen Fehlschlag fuer eine Buchung haelt.
     *
     * ZWEIERLEI TUT DIESER WEG DOCH MIT DEM ERGEBNIS:
     *
     * Er raeumt nach einem Erfolg den Hinweis weg. Der Hook tut das nicht mehr
     * von sich aus (ein Erfolg im Lauf der Warteschlange loeschte sonst den
     * Fehlschlag davor), und hier steht genau EINE Entscheidung — glueckt sie,
     * gehoert der alte Klartext nicht mehr an die Karte.
     *
     * Und er haengt bei JEDEM Fehlschlag „Bitte erneut versuchen." an den
     * Klartext. DIESER PFAD IST DER SOFORTIGE (heute nur „Ablehnen"): anders
     * als die Warteschlange oben fasst hier niemand von selbst nach — der
     * Hook (`entscheiden`) setzt zwar schon einen Hinweis, aber dessen
     * Klartext (aus `entscheideFreimeldungMitBudget`) verspricht bewusst
     * nichts mehr ueber einen weiteren Versuch. Ohne diese Zeile stuende an
     * der Karte nur die nackte Unklarheit, und der Koordinator wüsste nicht,
     * dass er selbst noch einmal klicken muss.
     */
    const freimeldungEntscheidenOhneErgebnis = useCallback(
        async (id: string, entscheidung: 'bestaetigt' | 'abgelehnt', kommentar?: string): Promise<void> => {
            // VOR DEM `await` FESTGEHALTEN. Auch dieser Weg hat eine Luecke:
            // waehrend der RPC laeuft, kann die Ansicht fort sein oder ein
            // anderes Projekt offenstehen. Der Hinweis gehoerte dann zu einer
            // Maske, die es nicht mehr gibt — oder er loeschte am neuen
            // Projekt den Hinweis, der dort gerade richtig steht. Die Buchung
            // selbst ist gelaufen und bleibt; nur der Klartext entfaellt.
            const montiertBeimStart = freimeldungMontiert.current;
            const projektBeimStart: string = freimeldungProjektJetzt.current;
            const ergebnis = await freimeldungEntscheiden(id, entscheidung, kommentar);
            if (
                !montiertBeimStart
                || !freimeldungMontiert.current
                || freimeldungProjektJetzt.current !== projektBeimStart
            ) return;
            if (ergebnis.ok === true) {
                freimeldungHinweisSetzen('');
            } else {
                freimeldungHinweisSetzen(`${ergebnis.klartext} Bitte erneut versuchen.`);
            }
        },
        [freimeldungEntscheiden, freimeldungHinweisSetzen],
    );

    const freimeldungNachSpeichern = useCallback(
        (id: string, itemId: string, entscheidung: 'bestaetigt' | 'abgelehnt', kommentar?: string) => {
            // DAS PROJEKT STEMPELT DAS MODUL, NICHT DIE LISTE. Die Mangelkarte
            // kennt nur ihren Mangel; das Projekt kennt allein diese Ansicht —
            // und zwar aus dem Ref, der bei jedem Render nachgezogen wird, so
            // dass der Stempel nicht aus einer alten Closure stammt.
            const projectId = freimeldungProjektJetzt.current;
            // NICHT MEHR STILL. `warteschlangeEinreihen` wirft einen Auftrag
            // ohne Kennung, ohne Mangel oder ohne Projekt weg — frueher
            // wortlos: der Koordinator klickte „Bestätigen", nichts wurde
            // gebucht, und die Karte sah hinterher aus wie vorher. Geprueft
            // wird hier dasselbe wie dort, damit der Hinweis steht, bevor der
            // Auftrag verschwindet.
            if (!(id || '').trim() || !(itemId || '').trim() || !(projectId || '').trim()) {
                console.warn('Freimelde-Auftrag unvollständig, nicht eingereiht.', { id, itemId, projectId });
                freimeldungNachweisGescheitert();
                return;
            }
            freimeldungWarteschlangeSetzen(
                warteschlangeEinreihen(freimeldungWarteschlange.current, { id, itemId, projectId, entscheidung, kommentar }),
            );
            // Sofort einmal versuchen: „Zur Kenntnis genommen" steht an einem
            // Mangel, der laengst gespeichert sein kann — dann ist die Meldung
            // nach einer Lesefrage weg statt erst nach dem naechsten Save.
            // Dasselbe gilt fuer eine verwaiste Meldung (`fehlt`) und meist
            // auch fuer eine laengst herabgestufte Information (`sonstig`).
            // Beim Freimelde-Dialog laeuft der Versuch ins Leere (der Mangel
            // ist gerade erst geschlossen worden), das kostet eine Abfrage.
            void freimeldungWarteschlangeAbarbeiten();
        },
        [freimeldungWarteschlangeSetzen, freimeldungWarteschlangeAbarbeiten, freimeldungNachweisGescheitert],
    );

    // Das Speichersignal bleibt als AUSLOESER (es ist billig und kommt
    // meistens genau richtig) — es entscheidet nur nichts mehr allein.
    useEffect(() => {
        if (saveStatus !== 'saved') return;
        void freimeldungWarteschlangeAbarbeiten();
    }, [saveStatus, freimeldungWarteschlangeAbarbeiten]);

    // Nachfassen, solange etwas wartet: der Save, der unsere Aenderung
    // hochlaedt, muss kein 'saved' mehr an diese Ansicht melden (ein Flush fuer
    // ein anderes Projekt kann dazwischenliegen). Ohne die Uhr bliebe die
    // Entscheidung bis zur naechsten Bearbeitung liegen.
    useEffect(() => {
        // KEINE UHR IN DER NUR-ANSICHT. Kippt die Projektsperre, waehrend etwas
        // wartet, raeumt der naechste Render den Timer ab (`isReadOnly` steht in
        // den Abhaengigkeiten) — sonst bestaetigte er im Hintergrund weiter,
        // obwohl das Modul nichts mehr speichern darf. Die Schlange bleibt
        // dabei gefuellt und wird nachgeholt, sobald wieder geschrieben werden
        // darf.
        if (isReadOnly || freimeldungWartend === 0) return;
        const uhr = setInterval(() => { void freimeldungWarteschlangeAbarbeiten(); }, FREIMELDUNG_NACHFASS_MS);
        return () => clearInterval(uhr);
    }, [isReadOnly, freimeldungWartend, freimeldungWarteschlangeAbarbeiten]);

    // ─── „+" der Handy-Navigation (Ereignis 'osia:neuer-eintrag') ─────────────
    // App.tsx fragt zuerst hier. Solange das Prüfmodul steht, gehört das „+"
    // ihm — preventDefault meldet „erledigt", die App sucht dann weder Standort
    // noch wechselt sie das Projekt (ein Wechsel risse offene Dialoge mit):
    //  - Entwurf beschreibbar, nichts anderes offen → Mangel-Assistent auf dem
    //    Reiter „Mängel";
    //  - Nur-Ansicht → derselbe Hinweis wie beim Erfassen;
    //  - kein offener Entwurf (versendet, Nr. 0) → zurück in den Hub, dessen
    //    Begehungsknopf die nächste Begehung anbietet. Neue Mängel gehören nie
    //    still in einen versendeten Bericht;
    //  - Laden, Versand, Vorschau, Rückfrage offen → nichts.
    // Beide Hooks VOR den frühen Returns; der Rückruf nutzt nur, was darüber steht.
    const neuerEintragRef = useRef<(e: Event) => void>(() => {});
    useEffect(() => {
        neuerEintragRef.current = (e: Event) => {
            e.preventDefault();
            if (isLoading || !report || showMobileWizard) return;
            // Ein offener Dialog der Liste (Freimelden, Aktionsblatt, Rückfrage)
            // ginge beim Öffnen des Assistenten samt Eingaben verloren.
            if (offenerDialogImDom(document)) return;
            if (isSendFlowBusy || showEmailModal || showPdfPreview || draftDiscard || showParticipantPrompt) return;
            if (isReadOnly) { openMobileWizard(); return; }
            if (!istEntwurf) { onBack(); return; }
            if (activeSection !== 'items') onSectionChange('items');
            openMobileWizard();
        };
    });
    useEffect(() => {
        if (!isMobile) return;
        const beiNeuemEintrag = (e: Event) => neuerEintragRef.current(e);
        window.addEventListener('osia:neuer-eintrag', beiNeuemEintrag);
        return () => window.removeEventListener('osia:neuer-eintrag', beiNeuemEintrag);
    }, [isMobile]);

    if (isLoading || !report) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-slate-50 text-slate-500">
                <Loader2 className="w-10 h-10 animate-spin mb-4 text-blue-600" />
                <p className="font-medium">Synchronisiere Mängel...</p>
            </div>
        );
    }
    
    // --- SPECIAL MOBILE WIZARD OVERLAY ---
    //
    // Frueher stand hier `showMobileWizard && !isReadOnly`. Kippte die
    // Projektsperre mitten in der Erfassung (Admin bricht auf, eigener
    // Herzschlag bleibt aus), verschwand der Assistent wortlos — und mit ihm
    // alles, was gerade in seiner Maske stand. Jetzt bleibt die Maske stehen,
    // nur das Speichern faellt weg; den Grund nennt der rote Balken darin.
    // Regel und Wortlaut: services/mobileWizardLock.ts.
    const wizardGuard = decideMobileWizardGuard(showMobileWizard, isReadOnly, bannerNotice);
    if (wizardGuard.open) {
        return (
            <MobileInspectionWizard
                onSave={handleMobileWizardSave}
                onCancel={() => { setShowMobileWizard(false); setWizardInitialData(null); }}
                contacts={report.metadata.distributionList || []}
                plans={report.metadata.plans || []}
                initialData={wizardInitialData || undefined}
                reportDate={report.metadata.date}
                saveBlockedNotice={wizardGuard.canSave ? null : wizardGuard.notice}
                appMode={userProfile?.app_mode || 'sigeko'}
                language={report.metadata.reportLanguage}
                openItems={report.items.filter(i => i.status === 'open')}
                onCloseItem={handleMobileWizardClose}
                verortung={report.metadata.verortung}
                sitzungSchluessel={project.id}
                // Plan direkt aus „Auf Plan markieren“ hochladen — nicht im Lesemodus
                // (dann fehlt auch das Upload-Ziel uploadProjectId).
                onPlanAnlegen={isReadOnly ? undefined : planHinzufuegen}
                projectId={uploadProjectId}
                sectionId={report.metadata.sectionId && report.metadata.sectionId !== 'all' ? report.metadata.sectionId : undefined}
            />
        );
    }

    /**
     * Vermerkt die uebernommenen Paare mit `uebernommen_matrix_am`.
     *
     * Ohne diesen Stempel faellt dieselbe Zeile beim naechsten Oeffnen wieder
     * als Kandidat an (`uebernahmeKandidaten`): das Band boete die Uebernahme
     * erneut an, ein zweiter Lauf ueberschriebe von Hand nachgebesserte Zellen,
     * und niemand saehe, dass die Bewertung bereits in der Matrix steht.
     *
     * Der Zustand wird erst nach der Quittung der Datenbank nachgezogen — ein
     * optimistischer Vermerk liesse das Band verschwinden, obwohl in der
     * Koordinierung nichts steht.
     *
     * `allSettled` statt `all`: die Zeilen sind voneinander unabhaengig, jede
     * ist eine eigene Fahrt. Bei `all` haetten die bereits gestempelten Zeilen
     * ihren Stempel in der Datenbank, im Zustand aber nicht — das Band boete
     * sie erneut an, obwohl der Server sie schon als uebernommen fuehrt.
     */
    const handleKoordinierungUebernommen = (paarIds: string[]) => {
        if (isReadOnly || !paarIds.length) return;
        const jetztIso = new Date().toISOString();
        void (async () => {
            try {
                const ergebnisse = await Promise.allSettled(paarIds.map(id =>
                    updateKoordinierungPaar(id, { uebernommen_matrix_am: jetztIso }, { isReadOnly }),
                ));
                const gelungen = ergebnisse.flatMap(r => (r.status === 'fulfilled' ? [r.value] : []));
                if (gelungen.length) {
                    const nachId = new Map(gelungen.map(p => [p.id, p]));
                    setKoordinierungPaare(prev => prev.map(p => nachId.get(p.id) ?? p));
                }
                const gescheitert = ergebnisse.filter(r => r.status === 'rejected');
                if (!gescheitert.length) return;
                gescheitert.forEach(r => console.error('Vermerk „in Matrix übernommen" fehlgeschlagen', (r as PromiseRejectedResult).reason));
                // Die Gefaehrdungen stehen bereits in der Matrix (onChange lief
                // vorher). Gemeldet wird nur, was WIRKLICH offen blieb — nur
                // diese Zeilen kommen beim naechsten Oeffnen wieder.
                alert(`Die Gefährdungen stehen in der Matrix. Bei ${gescheitert.length} von ${paarIds.length} Zeilen ließ sich der Vermerk in der Gewerke-Überschneidung nicht speichern; genau diese werden beim nächsten Öffnen erneut angeboten.`);
            } catch (e) {
                console.error('Vermerk „in Matrix übernommen" fehlgeschlagen', e);
                alert('Die Gefährdungen stehen in der Matrix, der Vermerk in der Gewerke-Überschneidung ließ sich aber nicht speichern. Beim nächsten Öffnen wird die Übernahme erneut angeboten.');
            }
        })();
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'items':
                return (
                    <div className="space-y-6">
                        {/* Ueber der Liste, nicht darin: Links und verwaiste
                            Meldungen gehoeren zum Bericht, nicht zu einem
                            einzelnen Mangel. Ohne Links und ohne Meldungen
                            zeichnet das Panel gar nichts. */}
                        <FreimeldungPanel
                            links={freimeldungLinks}
                            verwaiste={verwaisteFreimeldungen}
                            offene={offeneFreimeldungen}
                            laedt={freimeldungenLaden}
                            fehler={freimeldungFehler}
                            entscheidungsFehler={freimeldungEntscheidungsFehler}
                            onReload={freimeldungenNeuLaden}
                            onEntscheiden={freimeldungEntscheidenOhneErgebnis}
                            // „Erledigt" an einer verwaisten Meldung geht ueber
                            // denselben Nachweis wie jede andere Bestaetigung:
                            // der Mangel fehlt serverseitig erst, wenn das
                            // Loeschen (oder der Umzug) hochgeladen ist.
                            onNachSpeichern={freimeldungNachSpeichern}
                            isReadOnly={isReadOnly}
                        />
                        <InspectionList
                            key="active"
                            items={report.items}
                            onMobileAdd={handleAddItemWrapper}
                            onChange={(items) => handleUpdate({ items })}
                            onItemsAendern={maengelAktuellAendern}
                            contacts={report.metadata.distributionList || []}
                            plans={report.metadata.plans || []}
                            filterMode="active"
                            language={report.metadata.reportLanguage}
                            appMode={userProfile?.app_mode}
                            activeSectionId={report.metadata.sectionId}
                            reportDate={report.metadata.date}
                            projectId={uploadProjectId}
                            freimeldungen={freimeldungenJeItem}
                            freimeldungFehler={freimeldungEntscheidungsFehler}
                            onFreimeldungEntscheiden={isReadOnly ? undefined : freimeldungEntscheidenOhneErgebnis}
                            onFreimeldungNachSpeichern={isReadOnly ? undefined : freimeldungNachSpeichern}
                            verortung={report.metadata.verortung}
                            onPlanAnlegen={isReadOnly ? undefined : planHinzufuegen}
                        />
                    </div>
                );
            case 'contacts':
                return (
                    <ContactsView 
                        contacts={report.metadata.distributionList || []} 
                        onChange={handleContactsUpdate}
                        settings={report.settings}
                        activeSectionId={report.metadata.sectionId}
                        sections={report.metadata.globalData?.sections}
                        guests={report.metadata.guests || []}
                        onParticipantsChange={handleParticipantsUpdate}
                        isReadOnly={isReadOnly}
                    />
                );
            case 'checklists':
                return isMobile ? (
                    <MobileChecklistMode
                        entries={report.checklists}
                        onChange={(checklists) => handleUpdate({ checklists })}
                        reportDate={report.metadata.date}
                        activeSectionId={report.metadata.sectionId}
                        syncStatus={saveStatus}
                        onCreateDefect={(initial) => openMobileWizard(initial)}
                        language={report.metadata.reportLanguage}
                    />
                ) : (
                    <ChecklistView
                        entries={report.checklists}
                        onChange={(checklists) => handleUpdate({ checklists })}
                        reportDate={report.metadata.date}
                        activeSectionId={report.metadata.sectionId}
                        language={report.metadata.reportLanguage}
                    />
                );
            case 'plans':
                return (
                    <>
                    <SafetyPointsView 
                        plans={report.metadata.plans || []} 
                        points={report.safetyPoints || []}
                        items={report.items}
                        onChange={(points) => handleUpdate({ safetyPoints: points })}
                        // Ohne Callback blendet die Ansicht die Plan-Verwaltung aus: das
                        // Hochladen eines Plans landet sonst im Storage, der Plan selbst
                        // aber nirgends.
                        onPlansChange={isReadOnly ? undefined : (plans) => handleUpdate({ metadata: { ...report.metadata, plans } })}
                        activeSectionId={report.metadata.sectionId}
                        projectId={project.id}
                    />
                    {/* Sensor-Verortung: Geschosse, Geruest, Plan-Kalibrierung, Projektschalter
                        (docs/verortung-kamera-vertrag.md). Liegt in metadata.verortung. */}
                    <div className="mt-4">
                        <VerortungEinrichtung
                            verortung={verortungLesen(report.metadata.verortung)}
                            plaene={report.metadata.plans || []}
                            onChange={isReadOnly ? undefined : (verortung) => handleUpdate({ metadata: { ...report.metadata, verortung } })}
                        />
                    </div>
                    </>
                );
            case 'interactions':
                return (
                    <>
                        {/* Ruhig, aber sichtbar: „keine bestaetigten Paare" und
                            „konnte nicht geladen werden" sehen sonst gleich aus.
                            Die noch nicht gefahrene Migration Nr. 40 landet hier
                            NICHT — die faengt der Dienst selbst ab. */}
                        {koordinierungFehler && (
                            <div className="max-w-7xl mx-auto mb-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                                Die bestätigten Gewerkepaare der Koordinierung konnten nicht geladen werden.
                                Die Matrix zeigt deshalb nur, was hier bereits erfasst ist.
                            </div>
                        )}
                        <HazardMatrixView
                            hazards={report.mutualHazards || []}
                            contacts={report.metadata.distributionList || []}
                            onChange={(mutualHazards) => handleUpdate({ mutualHazards })}
                            koordinierungPaare={koordinierungPaare}
                            // Ohne Callback blendet die Matrix die Uebernahme aus
                            // — sie prueft ausdruecklich auf diese Prop. Im
                            // Lesemodus stuende der Knopf sonst da und schriebe
                            // die Gefaehrdungen in den Bericht, ohne dass der
                            // Vermerk in der Koordinierung entstuende.
                            onKoordinierungUebernommen={isReadOnly ? undefined : handleKoordinierungUebernommen}
                        />
                    </>
                );
            case 'analysis': return (
                <AnalysisView
                    items={report.items}
                    // Ohne Callback blendet die Auswertung „Firmen zusammenfuehren" aus:
                    // im Lesemodus liefe der Assistent sonst optisch durch, ohne zu speichern.
                    onItemsChange={isReadOnly ? undefined : (items) => handleUpdate({ items })}
                    contacts={report.metadata.distributionList || []}
                />
            );
            case 'project-map': return <ProjectLocationView report={report} onUpdate={handleUpdate} />;
            // Auch aus der Historie heraus wird freigemeldet und ein Datum
            // korrigiert — ohne reportDate stuende dort der Tag der Nacharbeit.
            case 'history': return <InspectionList key="history" items={report.items} onChange={(items) => handleUpdate({ items })} onItemsAendern={maengelAktuellAendern} contacts={report.metadata.distributionList || []} plans={report.metadata.plans || []} filterMode="history" language={report.metadata.reportLanguage} appMode={userProfile?.app_mode} reportDate={report.metadata.date} projectId={uploadProjectId} freimeldungen={freimeldungenJeItem} freimeldungFehler={freimeldungEntscheidungsFehler} onFreimeldungEntscheiden={isReadOnly ? undefined : freimeldungEntscheidenOhneErgebnis} onFreimeldungNachSpeichern={isReadOnly ? undefined : freimeldungNachSpeichern} />;
            case 'archive': return (
                <HistoryView
                    history={history}
                    // Im Lesemodus ohne Selbstheilung: report_history ist das
                    // Archiv versendeter Dokumente, daran wird nichts
                    // nachtraeglich geschrieben.
                    resolveReport={(id) => loadReportSnapshot(id, project.id, { skipMigration: isReadOnly })}
                    onLoad={() => {}} // No restore
                    onGeneratePdf={(r) => {
                        setPreviewReport(r);
                        setShowPdfPreview(true);
                    }}
                    onDelete={handleDeleteHistory}
                    // Im Lesemodus blendet die Archivliste den Loesch-Knopf aus
                    // und stellt die Rueckfrage gar nicht erst: erst
                    // "unwiderruflich löschen?" fragen und dann absagen liest
                    // sich wie ein Fehler.
                    readOnly={isReadOnly}
                    // Ohne projectId/currentItems blendet das Archiv "Mängel
                    // wiederherstellen" aus: restoreInspectionItems schreibt direkt
                    // in die Cloud, am Lesemodus vorbei.
                    projectId={isReadOnly ? undefined : project?.id}
                    // Rein lesend und deshalb OHNE den Lesemodus-Riegel: nur
                    // fuer das Nachschlagen des Freimelde-QR-Codes im
                    // Archiv-Export. Haenge es nicht an projectId — daran
                    // haengt das Schreiben.
                    qrProjectId={project?.id}
                    // Der Schalter kommt aus dem LAUFENDEN Bericht, nicht aus
                    // den Archiv-Snapshots der Liste: `report.metadata` ist der
                    // aktuelle Projektstand, ein Snapshot der vom Versandtag.
                    // Nach dem Abschalten sind die gedruckten Codes tot — das
                    // Archiv-PDF darf sie dann nicht mehr mitdrucken.
                    freimeldungAktiv={istFreimeldungAktiv(report.metadata)}
                    currentItems={isReadOnly ? undefined : report.items}
                    // Nummernvergabe und Aufnahme in einem Zug, gegen den Bestand
                    // aus dem Ref: die Wiederherstellung hat vorher auf den
                    // Snapshot und auf die Rueckfrage gewartet, in dieser Zeit
                    // kann ein mobiler Foto-Upload fertig geworden sein. Der
                    // Rueckgabewert sagt der Ansicht, was wirklich vergeben wurde.
                    onItemsRestored={uebernehmeWiederhergestellteEintraege}
                    embedded
                />
            );
            case 'settings': return <SettingsForm settings={report.settings} onSave={(settings) => handleUpdate({ settings })} onClose={() => onSectionChange('items')} onMergeReport={handleMergeReport} />;
            // „PDF Export" aus der Seitenleiste: die Vorschau ging beim Wechsel
            // hierher schon auf (siehe vorigerAbschnitt), ihr Schließen führt
            // zurück. Zu sehen ist die Karte nach dem Wiederherstellen mit 'export'.
            case 'export': return (
                <div className="max-w-md mx-auto mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col items-center text-center gap-3">
                    <div className="bg-slate-100 p-3 rounded-xl text-slate-600"><FileText className="w-6 h-6" /></div>
                    <h3 className="font-bold text-slate-800">PDF Export</h3>
                    <p className="text-sm text-slate-500">Bericht #{report.metadata.inspectionNumber} in der Vorschau prüfen, dann herunterladen bzw. teilen.</p>
                    <button
                        type="button"
                        onClick={() => { setPreviewReport(null); setShowPdfPreview(true); }}
                        className="min-h-[44px] px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors"
                    >
                        <FileText className="w-4 h-4" /> PDF Vorschau öffnen
                    </button>
                </div>
            );
            default: return <div>Sektion nicht gefunden: {activeSection}</div>;
        }
    };

    return (
        <div data-glass-view="module-sigeko-inspection" className="flex flex-col h-full bg-transparent relative animate-fade-in">
            <div data-glass="kopf" className="bg-slate-900 text-white p-4 shrink-0 flex items-center justify-between z-20">
                <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-4'} min-w-0`}>
                    <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-full transition-colors shrink-0"><ArrowLeft className="w-5 h-5" /></button>
                    <div className="min-w-0">
                         <div className="flex items-center gap-2 min-w-0">
                             <h2 className="font-semibold text-sm sm:text-lg leading-tight truncate" title={report.metadata.projectName || 'Unbenanntes Projekt'}>
                                 {report.metadata.projectName || 'Unbenanntes Projekt'}
                             </h2>
                         </div>
                         <div className={`text-xs text-slate-300 flex items-center gap-2 mt-0.5 ${isMobile ? 'flex-wrap gap-y-1' : ''}`}>
                             <div className="flex items-center gap-2 min-w-0">
                                 <span data-tutorial="begehung.nummer" className="font-semibold text-slate-100 shrink-0">Bericht #{report.metadata.inspectionNumber}</span>
                                 {/* Handy: ohne Trennpunkt — die 12 px gehen ans Datum. */}
                                 {!isMobile && <span className="shrink-0">•</span>}
                                 <span className="truncate">{new Date(report.metadata.date).toLocaleDateString()}</span>
                             </div>
                             {/* Sprachknopf und „Entwurf verwerfen" bilden EINE Gruppe: bricht
                                 der Kopf am Handy um, wandern sie zusammen in die nächste Zeile.
                                 Getrennt umgebrochen, überlappten ihre nach oben und unten
                                 verlängerten Trefferflächen (gap-y-1) — ein Tipp auf DE/EN
                                 hätte den Entwurf verworfen. Nebeneinander stoßen sie nur an:
                                 der Rand links am Papierkorb (-ml-2) ist so breit wie gap-2. */}
                             <div className="flex items-center gap-2 shrink-0">
                                 {/* Handy: Berichtssprache. Der DE/EN-Schalter rechts im Kopf
                                     ist nur am PC da; hier ein Knopf, der wechselt — beide
                                     Sprachen sichtbar, die aktive als heller Chip auf der
                                     dunklen Schiene. Sie bestimmt Katalog, KI-Vorschlag,
                                     Checkliste und PDF, nicht die Bedienung.
                                     Trefferfläche 44 px nur in der Höhe, wie „Entwurf verwerfen". */}
                                 {isMobile && (
                                     <button
                                         type="button"
                                         onClick={() => handleMetadataUpdate({ reportLanguage: report.metadata.reportLanguage === 'en' ? 'de' : 'en' })}
                                         title="Berichtssprache (Katalog, KI-Vorschlag, Checkliste, PDF)"
                                         aria-label={report.metadata.reportLanguage === 'en' ? 'Berichtssprache Englisch, auf Deutsch umstellen' : 'Berichtssprache Deutsch, auf Englisch umstellen'}
                                         className="flex items-center py-4 -my-4 shrink-0"
                                     >
                                         <span className="flex items-center gap-0.5 rounded-md bg-slate-800 p-0.5 text-[11px] font-semibold uppercase">
                                             <span className={`px-1.5 py-px rounded ${report.metadata.reportLanguage !== 'en' ? 'bg-slate-100 text-slate-900' : 'text-slate-300'}`}>DE</span>
                                             <span className={`px-1.5 py-px rounded ${report.metadata.reportLanguage === 'en' ? 'bg-slate-100 text-slate-900' : 'text-slate-300'}`}>EN</span>
                                         </span>
                                     </button>
                                 )}
                                 {/* Außerhalb der inneren Zeile: passt der Knopf am Handy nicht
                                     mehr daneben, bricht er um, statt unter „PDF"/„Senden" zu
                                     rutschen (der Tipp landete dort). Trefferfläche 44 px, der
                                     negative Rand hält die Zeile flach und den Zurück-Pfeil frei. */}
                                 {istEntwurf && (
                                     <button
                                         type="button"
                                         onClick={handleDiscardDraftStart}
                                         disabled={!!draftDiscard}
                                         title="Diesen Entwurf verwerfen. Berichtszähler und Termine springen auf den Stand vor dem Anlegen zurück."
                                         aria-label="Entwurf verwerfen"
                                         className={`flex items-center gap-1 rounded text-[11px] font-semibold text-slate-400 transition-colors shrink-0 disabled:opacity-50 ${isMobile ? 'py-4 -my-4 pl-2 -ml-2 pr-6 -mr-6 active:text-rose-300' : 'px-1.5 py-0.5 hover:text-white hover:bg-rose-600/70'}`}
                                     >
                                         {draftDiscard?.kind === 'checking' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                         <span className="hidden sm:inline">Entwurf verwerfen</span>
                                     </button>
                                 )}
                             </div>
                         </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                    {!isMobile && (
                        <div className="flex bg-slate-800 p-1 rounded-xl border border-slate-700">
                            <button data-tutorial="pdf.sprache-de" onClick={() => handleMetadataUpdate({ reportLanguage: 'de' })} className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase transition-all flex items-center gap-1.5 ${report.metadata.reportLanguage !== 'en' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`}><Globe className="w-3 h-3" /> DE</button>
                            <button data-tutorial="pdf.sprache-en" onClick={() => handleMetadataUpdate({ reportLanguage: 'en' })} className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase transition-all flex items-center gap-1.5 ${report.metadata.reportLanguage === 'en' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`}><Globe className="w-3 h-3" /> EN</button>
                        </div>
                    )}
                    
                    {!isMobile && (
                        <button data-tutorial="pdf.optionen" onClick={() => setShowPrintSettings(true)} className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700 transition-all text-xs font-bold"><SlidersHorizontal className="w-4 h-4" /><span className="hidden lg:inline">Optionen</span></button>
                    )}
                    
                    {!isMobile ? (
                        <button data-tutorial="pdf.vorschau" onClick={() => setShowPdfPreview(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 rounded-xl transition-all text-xs font-bold group"><FileText className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" /><span>PDF Vorschau</span></button>
                    ) : (
                        // Handy: Vorschau neben „Senden" — vor dem Versand sehen, was hinausgeht.
                        // Nur als Symbol (44 × 44): mit Text wurde der Titelblock so schmal,
                        // dass „Entwurf verwerfen" unter diesen Knopf rutschte.
                        <button data-tutorial="pdf.vorschau" onClick={() => setShowPdfPreview(true)} aria-label="PDF Vorschau" title="PDF Vorschau" className="flex items-center justify-center w-11 h-11 shrink-0 bg-slate-800 active:bg-slate-700 text-slate-200 rounded-xl transition-colors"><FileText className="w-5 h-5" /></button>
                    )}
                    
                    {/* Handy: nur „Senden" ohne Brief, das Symbol wiederholte das Wort.
                        Der Ladekreis während des Versands bleibt in beiden Fällen. */}
                    <button
                        data-tutorial="versand.abschliessen-senden"
                        onClick={handleFinishAndSend}
                        disabled={isSendFlowBusy || isReadOnly}
                        title={isReadOnly ? 'Projekt ist schreibgeschützt geöffnet: kein Versand und keine Buchung möglich.' : undefined}
                        className={`flex items-center gap-2 ${isMobile ? 'px-4 min-h-[44px] text-sm' : 'px-4 text-xs'} py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-700 text-white rounded-xl transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed group`}
                    >{isSendFlowBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : !isMobile && <Mail className="w-4 h-4 text-blue-100 group-hover:text-white" />}<span>{isMobile ? 'Senden' : 'Abschließen & Senden'}</span></button>
                </div>
            </div>
            {/* Ohne diesen Hinweis waeren die gesperrten bzw. ausgeblendeten
              * Werkzeuge unerklaerlich — und Tipparbeit im Editor ginge
              * verloren, ohne dass es jemand ankuendigt. */}
            <ProjectNoticeBanner notice={bannerNotice} onTakeover={onTakeover} onJustify={onJustify} busy={takeoverBusy} />
            {isMobile && (
                <div className="bg-slate-900 px-3 pb-2.5 -mt-px shrink-0 z-10 flex items-center gap-1.5">
                    {/* Ohne Scrollbalken: dass es weitergeht, zeigt der angeschnittene
                        letzte Reiter. Das „!" ist nötig, weil index.css `* { scrollbar-width: thin }`
                        außerhalb jeder Ebene setzt; das schlägt jede Utility ohne „!". */}
                    <div className="flex-1 min-w-0 flex gap-1 overflow-x-auto [scrollbar-width:none]! [&::-webkit-scrollbar]:hidden">
                        {[
                            { id: 'items', label: 'Mängel' },
                            { id: 'checklists', label: 'Checklisten' },
                            { id: 'contacts', label: 'Beteiligte' },
                            { id: 'plans', label: 'Pläne' },
                            { id: 'interactions', label: 'Matrix' },
                            { id: 'analysis', label: 'Statistik' },
                            { id: 'history', label: 'Erledigt' },
                            { id: 'archive', label: 'Archiv' },
                        ].map(s => (
                            <button
                                key={s.id}
                                onClick={() => onSectionChange(s.id)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${activeSection === s.id ? 'bg-slate-100 text-slate-900' : 'text-slate-300 active:bg-slate-800'}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                    {/* Speicherstand am Handy (die Seitenleiste ist eingeklappt). Hier,
                        weil die Reiterleiste ihre Höhe nie ändert — im Kopf rutschte die
                        Plakette bei jedem Speichern in eine eigene Zeile, alles darunter
                        sprang mit. Einzeilig, lange Texte gekürzt. Der Checklistenmodus
                        zeigt ihn selbst. */}
                    {activeSection !== 'checklists' && <SyncBadge status={saveStatus} kurz />}
                </div>
            )}
            <div className="flex-1 overflow-hidden relative bg-transparent"><div className="h-full overflow-y-auto p-4 md:p-6">{renderContent()}</div></div>
            {showParticipantPrompt && !isReadOnly && (
                <ParticipantSheet
                    contacts={report.metadata.distributionList || []}
                    guests={report.metadata.guests || []}
                    onChange={handleParticipantsUpdate}
                    onClose={() => setShowParticipantPrompt(false)}
                    onSolo={() => setShowParticipantPrompt(false)}
                />
            )}
            {showPrintSettings && <PrintSettingsModal options={report.metadata.printOptions || { includeDistribution: true, includeChecklists: true, includeActiveItems: true, includeHistory: true, includePlans: true, includeHazards: true, includeStatistics: true }} onChange={(options) => handleMetadataUpdate({ printOptions: options })} berichtsDatum={report.metadata.date} onClose={() => setShowPrintSettings(false)} />}
            {/* freimeldungAktiv IMMER aus `report` (dem laufenden Bericht), nie
                aus `previewReport`: der Snapshot traegt den Schalterstand vom
                Versandtag und liesse die Vorschau nach dem Abschalten weiter
                einen toten QR-Code drucken. */}
            {showPdfPreview && <PdfPreviewModal report={previewReport || report} settings={report.settings} isReadOnly={isReadOnly} freimeldungAktiv={istFreimeldungAktiv(report.metadata)} conductedByName={previewReport ? undefined : buildActorName(userProfile)} onClose={() => { setShowPdfPreview(false); setPreviewReport(null); if (activeSection === 'export') onSectionChange(abschnittVorExport && abschnittVorExport !== 'export' ? abschnittVorExport : 'items'); }} onUpdate={(r) => { if (!previewReport) { setReport(r); onUpdate(r); } }} />}
            {sendGuardWarning && (
                <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div data-tutorial="versand.erneut-riegel" className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in border border-slate-200">
                        <div className={`${sendGuardWarning.hartBlockiert ? 'bg-rose-600' : 'bg-amber-600'} text-white p-5 flex items-center gap-3`}>
                            <AlertTriangle className="w-6 h-6 shrink-0" />
                            <h3 className="font-bold text-lg leading-tight">
                                {sendGuardWarning.hartBlockiert
                                    ? 'Übungsprojekt: kein Versand'
                                    : sendGuardWarning.reason === 'archive_mismatch' ? 'Archiv-Eintrag mit gleicher Nummer' : 'Bericht wurde bereits versendet'}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-700 leading-relaxed font-bold">{sendGuardWarning.warning}</p>
                            {/* HARTER RIEGEL: kein „trotzdem senden". Der Treffer ist
                                keine Abwägung, sondern eine Regel — dasselbe Muster wie
                                beim Freigabe-Riegel darunter. */}
                            <div data-tutorial="versand.doppelsende-riegel" className={sendGuardWarning.hartBlockiert ? 'bg-rose-50 border border-rose-200 rounded-xl p-3' : 'bg-amber-50 border border-amber-200 rounded-xl p-3'}>
                                {sendGuardWarning.hartBlockiert ? (
                                    <p className="text-[11px] text-rose-800 leading-relaxed">
                                        Aus einem Übungsprojekt geht nichts hinaus: die Empfänger der Vorlage sind
                                        erfundene Adressen. Alles andere funktioniert wie im echten Projekt:
                                        Bericht schreiben, Mängel erfassen, PDF ansehen. Die Datenbank hält
                                        dieselbe Regel ein zweites Mal.
                                    </p>
                                ) : sendGuardWarning.reason === 'archive_mismatch' ? (
                                    <p className="text-[11px] text-amber-800 leading-relaxed">
                                        Beim Empfänger ist kein Duplikat zu erwarten; der Versand wird als Erstversand
                                        behandelt und im Sendeverlauf <b>nicht</b> als Wiederholung vermerkt. Bei Unsicherheit
                                        vorher im <b>Sendeverlauf</b> prüfen, ob dieser Bericht schon einmal hinausging.
                                    </p>
                                ) : (
                                    <p className="text-[11px] text-amber-800 leading-relaxed">
                                        Ein erneuter Versand erzeugt beim Empfänger ein Duplikat. Bitte zuerst im <b>Sendeverlauf</b> prüfen,
                                        ob wirklich noch etwas fehlt. Nur wenn du bewusst erneut senden willst, den Bericht als
                                        Wiederholung verschicken. Sie wird dann intern im <b>Sendeverlauf</b> markiert, der
                                        Empfänger sieht davon nichts im Betreff.
                                    </p>
                                )}
                            </div>
                            {sendGuardWarning.hartBlockiert ? (
                                <button
                                    onClick={() => setSendGuardWarning(null)}
                                    className="w-full px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm transition-colors shadow-md"
                                >
                                    Verstanden
                                </button>
                            ) : (
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setSendGuardWarning(null)}
                                        className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        onClick={handleConfirmRepeatSend}
                                        className="flex-1 px-4 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm transition-colors shadow-md"
                                    >
                                        {sendGuardWarning.reason === 'archive_mismatch' ? 'Senden' : 'Bewusst erneut senden'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {releaseBlock && (
                <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div data-tutorial="begehung.freigabe-riegel" className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in border border-slate-200">
                        <div className="bg-rose-600 text-white p-5 flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6 shrink-0" />
                            <h3 className="font-bold text-lg leading-tight">Bericht noch nicht freigegeben</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-700 leading-relaxed font-bold">{releaseBlock.hinweis}</p>
                            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                                <p className="text-[11px] text-rose-800 leading-relaxed">
                                    Die Freigabe erfolgt in der Ansicht <b>Freelancer &rarr; Prüfung</b>.
                                    Erst nach „Freigeben (geprüft)" lässt sich der Bericht an den Kunden senden.
                                    Das ist die Vier-Augen-Prüfung und kein Hinweis, der sich übergehen lässt.
                                </p>
                            </div>
                            <button
                                onClick={() => setReleaseBlock(null)}
                                className="w-full px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm transition-colors shadow-md"
                            >
                                Verstanden
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Im Schreibschutz nur noch die Absage zeigen — der Bestaetigungs-
              * dialog darf dann nicht mehr offen stehen, die Absage schon. */}
            {draftDiscard && draftDiscard.kind !== 'checking' && (!isReadOnly || draftDiscard.kind === 'blocked') && (
                <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in border border-slate-200">
                        {draftDiscard.kind === 'blocked' ? (
                            <>
                                <div className="bg-slate-700 text-white p-5 flex items-center gap-3">
                                    <AlertTriangle className="w-6 h-6 shrink-0" />
                                    <h3 className="font-bold text-lg leading-tight">Entwurf lässt sich nicht verwerfen</h3>
                                </div>
                                <div className="p-6 space-y-4">
                                    <p className="text-sm text-slate-700 leading-relaxed font-bold">{draftDiscard.message}</p>
                                    <button
                                        onClick={() => setDraftDiscard(null)}
                                        className="w-full px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm transition-colors shadow-md"
                                    >
                                        Verstanden
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="bg-rose-600 text-white p-5 flex items-center gap-3">
                                    <Trash2 className="w-6 h-6 shrink-0" />
                                    <h3 className="font-bold text-lg leading-tight">Entwurf #{draftDiscard.plan.draftNr} verwerfen?</h3>
                                </div>
                                <div className="p-6 space-y-4">
                                    <p className="text-sm text-slate-700 leading-relaxed font-bold">{draftDiscard.plan.summary}</p>
                                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                                        <p className="text-[11px] text-rose-800 leading-relaxed">
                                            Alle Eingaben dieses Entwurfs gehen verloren
                                            {draftDiscard.plan.droppedItemIds.length > 0
                                                ? `, darunter ${draftDiscard.plan.droppedItemIds.length} ${draftDiscard.plan.droppedItemIds.length === 1 ? 'Eintrag, der' : 'Einträge, die'} erst in diesem Entwurf erfasst ${draftDiscard.plan.droppedItemIds.length === 1 ? 'wurde' : 'wurden'}.`
                                                : '.'}
                                            {' '}Positionen, Buchungen, Notizen und Kontakte des Projekts bleiben unberührt.
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setDraftDiscard(null)}
                                            disabled={draftDiscard.kind === 'busy'}
                                            className="flex-1 px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition-colors disabled:opacity-50"
                                        >
                                            Abbrechen
                                        </button>
                                        <button
                                            onClick={handleDiscardDraftConfirm}
                                            disabled={draftDiscard.kind === 'busy'}
                                            className="flex-1 px-4 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm transition-colors shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {draftDiscard.kind === 'busy' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                            Entwurf verwerfen
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {showBillingModal && !isReadOnly && (
                <BillingSelectionModal
                    isOpen={showBillingModal}
                    onClose={abortSendFlow}
                    onConfirm={handleConfirmMainBilling} 
                    services={availableInspectionServices} 
                    title={parentProjectData ? `Abrechnung über: ${parentProjectData.metadata.projectName}` : "Begehung abrechnen"} 
                    context="inspection"
                    blockedServices={blockedServices}
                    canSeeFinancials={rechte.hat('preise.sehen')}
                    /*
                     * Eine Begehung kann mehrere Lose abdecken — die Leistung
                     * wird dann je Los auf eine eigene LV-Position gebucht.
                     * NICHT bei Abrechnung ueber ein Mutterprojekt: dieser Pfad
                     * schreibt eine einzelne Buchung ins fremde Projekt und
                     * kennt keine Zeilen (siehe handleConfirmMainBilling).
                     */
                    allowSplit={report.metadata.globalData?.linkedProjectConfig?.role !== 'child'}
                    memoryScope={project.id}
                    preferredIds={preferredInspectionIds}
                />
            )}
            {showAdditionalModal && !isReadOnly && <AdditionalServicesModal isOpen={showAdditionalModal} onClose={abortSendFlow} onConfirm={handleConfirmAdditional} services={additionalServices} />}
            {/*
              * EmailModal kennt selbst keinen Lesemodus (es hat kein passendes
              * Prop): "Jetzt senden" liesse sich in einem bereits offenen
              * Fenster weiter druecken. Deshalb wird es hier gar nicht erst
              * gerendert — der Riegel greift auch, wenn der Lesemodus mitten im
              * Ablauf einsetzt.
              */}
            {showEmailModal && !isReadOnly && (
                <EmailModal
                    isOpen={showEmailModal}
                    onClose={abortSendFlow}
                    report={report} 
                    settings={report.settings} 
                    onUpdate={(r) => { setReport(r); onUpdate(r); }} 
                    projectId={project.id} 
                    activeSectionId={report.metadata.sectionId} 
                    precapturedHours={precapturedHours} 
                    additionalAddons={selectedAddons} 
                    precapturedServiceId={precapturedServiceId}
                    precapturedBookings={precapturedBookings}
                    onFinished={onBack}
                    userProfile={userProfile}
                    isRepeatSend={isRepeatSend}
                />
            )}
        </div>
    );
};
